import { Canvas, useFrame, type Camera, type RootState } from '@react-three/fiber';
import * as THREE from 'three';

import {
  DEFAULT_VIEWER_POSITION,
  hoverImageIdAtom,
  selectedLayersAtom,
  setProjectInitialPosition,
  updateViewerStateWithInitialPosition,
  userViewerSettingsAtom,
  viewerStateAtomFamily,
} from '@/atoms';
import { alignmentsAtom, alignmentWalkAtom } from '@/atoms/alignment.atom';
import { DEFAULT_ELEVATION_PROFILE_TOOL_MODE, elevationProfileToolModeAtom } from '@/atoms/elevationProfile.atom';
import { layersAtom, selectedLayerIdsAtom } from '@/atoms/layers.atom';
import { DEFAULT_PIPE_TOOL_MODE, pipeToolModeAtom } from '@/atoms/pipe.atom';
import { stationDockOpenAtom, stationVolumeDockAtom } from '@/atoms/stationVolumes.atom';
import { DEFAULT_STOCKPILE_TOOL_MODE, stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import {
  cameraMapStateAtom,
  clipBoxStateAtom,
  orthoMetersPerPixelAtom,
  viewerEvents,
  type ClipBoxInteractionMode,
  type ClipBoxMode,
} from '@/atoms/viewer.atom';
import { QuickUploadDropZone, QuickUploadSharedLinkCta } from '@/components/QuickUploadDropZone';
import { groupImagesByPosition2, project, registerProjection } from '@/core';
import { execute, getLinkSessionId } from '@/graphql/execute';
import { CustomBaseMap, GoogleImagesByPositionQuery } from '@/graphql/graphql';
import { useIsMobile } from '@/hooks/use-mobile';
import { useFeature } from '@/hooks/useFeature';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useOpenAnalysisReport } from '@/hooks/useOpenAnalysisReport';
import { useSavedViews } from '@/hooks/useSavedViews';
import { useViewCaptureApply } from '@/hooks/useViewCaptureApply';
import { useViewerHotkeys } from '@/hooks/useViewerHotkeys';
import { analysisColumnHasContent } from '@/lib/analysisColumn';
import { analytics, useFeatureFlagEnabled } from '@/lib/analytics';
import {
  cameraModeFromFlags,
  cameraModeToFlags,
  isOrtho,
  isThirdPerson,
  toggleOrtho,
  togglePerson,
  type CameraMode,
} from '@/lib/cameraMode';
import { cameraTransition, type CameraPose } from '@/lib/cameraTransition';
import type { DerivedClearanceLayerMetadata } from '@/lib/clearance/types';
import { lookAtCameraCommand } from '@/lib/lookAtCamera';
import { isTauri } from '@/lib/offline/isTauri';
import { useLayerFeatures } from '@/hooks/useLayerFeatures';
import { useStableMapOrigin } from '@/hooks/useStableMapOrigin';
import { isInternalUser } from '@/lib/email';
import { userAtom } from '@/atoms/user.atom';
import { showFpsAtom } from '@/atoms/viewerDebug.atom';
import { loadImagesByLayerOfflineAware } from '@/lib/offline/offlineImages';
import type { SavedViewCamera } from '@/lib/savedViews';
import { resolveScalarRangeWithOverride, type ScalarRangeMetadata } from '@/lib/scalarRange';
import type { StationVolumesLayerMeta } from '@/lib/stationVolumes/types';
import { cn } from '@/lib/utils';
import Layers from '@/pages/Project/Viewer/Layers';
import { googleImagesByPositionQuery } from '@/queries/googleImage.query';
import { getProjectQuery } from '@/queries/project.query';
import {
  AccessibilitySolid,
  BoundingBoxSolid,
  BoxSolid,
  CameraSolid,
  ChartLineSolid,
  CircleSolid,
  DownloadSolid,
  EyeSlashSolid,
  PanelRightCloseSolid,
  GitMergeSolid,
  Map,
  MountainSolid,
  RulerSolid,
  ToolSolid,
} from '@mynaui/icons-react';
import { PointColorType } from '@pnext/three-loader';
import { CameraControls, CameraControlsImpl, OrthographicCamera, Stats } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useQuery } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  FunctionComponent,
  lazy,
  Fragment,
  PropsWithChildren,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type SetStateAction,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlignmentOverlay } from './3D/AlignmentOverlay';
import AreaMeasuringTool from './3D/AreaMeasuringTool';
import { ClearanceEnvelope } from './3D/ClearanceEnvelopePreview';
import { CrestGuideTool } from './3D/CrestGuideTool';
import { CrestLinePreview } from './3D/CrestLinePreview';
import ElevationProfileTool from './3D/ElevationProfileTool';
import MeasuringTool from './3D/MeasuringTool';
import { PipeTool } from './3D/PipeTool';
import SavedElevationProfiles from './3D/SavedElevationProfiles';
import SavedMeasurements from './3D/SavedMeasurements';
import { SavedPipes } from './3D/SavedPipes';
import { SavedStockpiles } from './3D/SavedStockpiles';
import { SceneContextMenuPicker } from './3D/SceneContextMenuPicker';
import { SnapIndicator } from './3D/SnapIndicator';
import { StationBands } from './3D/StationBands';
import { StationSectionOverlay } from './3D/StationSectionOverlay';
import { StockpileTool } from './3D/StockpileTool';
import { StockpileVisualization } from './3D/StockpileVisualization';
import { SwathPreview } from './3D/SwathPreview';
import { AddLineTool } from './AddLineTool';
import { AddLineToolbar } from './AddLineToolbar';
import { AddPointToolbar } from './AddPointToolbar';
import { AddPolygonTool } from './AddPolygonTool';
import { AddPolygonToolbar } from './AddPolygonToolbar';
import { AddSurfaceTool } from './AddSurfaceTool';
import { AddSurfaceToolbar } from './AddSurfaceToolbar';
import { AlignmentWalkToolbar, stepStation } from './AlignmentWalkToolbar';
import { CameraModeSwitch } from './CameraModeSwitch';
import { ClearanceDock } from './ClearanceDock';
import { ClearancePanel } from './ClearancePanel';
import ClipBoxExportDialog from './ClipBoxExportDialog';
import { ClipBoxTool } from './ClipBoxTool';
import CoordinateInfoBox from './CoordinateInfoBox';
import { CrestGuideToolbar } from './CrestGuideToolbar';
import { DeviationColorbar, scalarBarFormat } from './DeviationColorbar';
import { DeviationVolumePanel } from './DeviationVolumePanel';
import { showDeviationVolumes } from '@/lib/analyses/compare/deviationVolumes';
import { comparisonReportTarget, stackCutFillFirst } from '@/lib/analyses/compare/pairing';
import { ElevationProfileDrawingHint } from './ElevationProfileDrawingHint';
import { ElevationProfilePanel } from './ElevationProfilePanel';
import FeatureProperties from './FeatureProperties';
import FragmentProperties from './FragmentProperties';
import FragmentSpatialTree from './FragmentSpatialTree';
import { GeoJson } from './GeoJson';
import { GLBModel } from './GLBModel';
import GoogleStreetView from './GoogleStreetView';
import { IFC } from './IFC';
import { IFC2 } from './IFC2';
import { IPRJ } from './IPRJ2';
import { LandXML } from './LandXML';
import LandXMLProperties from './LandXMLProperties';
import LayerProperties from './LayerProperties';
import { Loader } from './Loader';
import OrthoGrid from './OrthoGrid';
import OrthoScaleBar from './OrthoScaleBar';
import { PipeDetectPanel } from './PipeDetectPanel';
import { PointCloud } from './Pointcloud';
import PortalTooltip from './PortalTooltip';
import { RainfallHeatmapLegend } from './RainfallHeatmapLegend';
import { RainfallOverflowArrows3D } from './RainfallOverflowArrows3D';
import { RainfallPlaybackBarMount } from './RainfallPlaybackBar';
import { RainfallPondFillAnimation3D } from './RainfallPondFillAnimation3D';
import { RainfallSummaryPanel } from './RainfallSummaryPanel';
import { LazySplat } from './SplatLazy';
import { ViewerLoadingColumn } from './ViewerLoadingColumn';
import { orbitDistanceOrDefault } from '@/lib/orbitDistance';
import { StationVolumeDock } from './StationVolumeDock';
import { StockpileComputePanel } from './StockpileComputePanel';
import { StockpileToolbar } from './StockpileToolbar';
import { FeatureGate } from './subscription/FeatureGate';
import { SurfaceDifferencePanel } from './SurfaceDifferencePanel';
import { Joystick } from './Viewer/Joystick';
import { ViewerContextMenu } from './ViewerContextMenu';
import { AnalysisColumnWindow, MinimapWindow, WalkToolbarWindow } from './ViewerWindows';
import { Xgrids } from './Xgrids';
const MapView = lazy(() => import('./MapView'));

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

interface iViewerProps extends PropsWithChildren {
  projectId: string;
  hideMap?: boolean;
  showImages?: boolean;
  hideLayers?: boolean;
}

type StoredCameraState = {
  azimuth: number;
  polar: number;
  distance: number;
  zoom: number;
  position: [number, number, number];
  target: [number, number, number];
  thirdPerson?: boolean;
  ortho?: boolean;
};

/** A boolean slice of the dock atom, so dragging the dock's height does not
 *  re-render the whole viewer — only opening/closing the clearance dock does. */
const clearanceDockOpenAtom = atom((get) => get(stationVolumeDockAtom)?.dataset === 'clearance');

const Viewer: FunctionComponent<iViewerProps> = ({
  projectId,
  children,
  hideMap = false,
  showImages = true,
  hideLayers = false,
}) => {
  const [hidePointcloud, setHidePointcloud] = useState(false);
  // Hides the floating analysis UI (the analysis column + rainfall heatmap
  // legends) so only the result itself is on screen — e.g. for screen
  // recordings. Session-only on purpose: a reload brings the panels back.
  const [hideAnalysisPanels, setHideAnalysisPanels] = useState(false);
  const [clipBoxState, setClipBoxState] = useAtom(clipBoxStateAtom(projectId));
  const clipBoxEnabled = clipBoxState.enabled;
  const clipBoxMode = clipBoxState.clipMode;
  const [viewerState, setViewerState] = useAtom(viewerStateAtomFamily(projectId));
  const showGoogleStreetView = viewerState.showGoogleStreetView;
  const setShowGoogleStreetView = useCallback(
    (value: boolean) => setViewerState((prev) => ({ ...prev, showGoogleStreetView: value })),
    [setViewerState],
  );
  const [showMap, setShowMap] = useState(true);
  const storedCameraState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(`@actegon.gis.cameraState.${projectId}`);
      return raw ? (JSON.parse(raw) as StoredCameraState) : null;
    } catch {
      return null;
    }
  }, [projectId]);
  // One three-state mode replaces the `orthoCamera` / `thirdPersonFlying` pair
  // this used to be (see `lib/cameraMode.ts`). The booleans live on only as the
  // persisted shape — sessionStorage here, `SavedViewCamera` in the database.
  const [cameraMode, setCameraModeState] = useState<CameraMode>(() =>
    cameraModeFromFlags({
      ortho: storedCameraState?.ortho ?? false,
      thirdPerson: storedCameraState?.thirdPerson ?? false,
    }),
  );
  const orthoCamera = isOrtho(cameraMode);
  // Which perspective mode `O` returns to when it leaves ortho. Not persisted:
  // after a reload ortho falls back to third person, its closest neighbour.
  const previousPerspectiveRef = useRef<CameraMode>(cameraMode === 'ORTHO' ? 'THIRD_PERSON' : cameraMode);
  const [showJoystick, setShowJoystick] = useState(true);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  // Phone-width viewports: one row of every tool runs off the screen (taking
  // the camera switch, the last item, with it), and touch has no hover to show
  // what an icon does. The bar keeps only Tools + camera; the rest go into a
  // labelled bottom sheet.
  const isNarrow = useIsMobile();
  const [toolsSheetOpen, setToolsSheetOpen] = useState(false);
  const isGoogleStreetViewEnabled = useFeatureFlagEnabled('googleStreetview');
  const { t } = useTranslation('project');
  const { t: tFg } = useTranslation('featureGate');

  useEffect(() => {
    const checkTouchDevice = () => {
      // More comprehensive touch detection for iOS Safari and other devices
      const hasTouch =
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore - for older browsers
        (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0) ||
        // Check for iOS specifically
        (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream);

      setIsTouchDevice(hasTouch);
    };
    checkTouchDevice();
  }, []);

  const [params, setParams] = useSearchParams();
  const isMeasurementsEnabled = params.get('showMeasurements') === 'true';
  const isAreaMeasurementEnabled = params.get('showAreaMeasurement') === 'true';
  const [elevationProfileMode, setElevationProfileMode] = useAtom(elevationProfileToolModeAtom);

  const [positionWGS84, setPositionWGS84] = useState<THREE.Vector3 | null>(null);

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });

  // Register the projection from the server when project data is available
  useEffect(() => {
    if (projectData?.project?.srid && projectData.project.srid !== -1) {
      registerProjection(projectData.project.srid, projectData.project.proj4String);
    }
  }, [projectData?.project?.srid, projectData?.project?.proj4String]);

  // Project-level "default 3rd person view": applies only on the first load of
  // a session — a stored camera state (the user already navigated) wins.
  // One-shot effect because project data arrives async.
  const appliedDefaultThirdPersonRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultThirdPersonRef.current || !projectData?.project) return;
    appliedDefaultThirdPersonRef.current = true;
    if (!storedCameraState && projectData.project.defaultThirdPersonView) {
      setCameraModeState('THIRD_PERSON');
    }
  }, [projectData?.project, storedCameraState]);

  const { data: googleImagesData } = useQuery({
    queryKey: ['googleImagesByPosition', positionWGS84],
    queryFn: () => execute(googleImagesByPositionQuery, { lat: positionWGS84!.y, lng: positionWGS84!.x }),
    enabled: positionWGS84 !== null && showGoogleStreetView,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (googleImagesData?.images.length === 0) {
      toast.error(t('viewer3d.noStreetViewNearby'));
      setShowGoogleStreetView(false);
    }
  }, [googleImagesData?.images]);

  const [googleImages, setGoogleImages] = useState<
    (GoogleImagesByPositionQuery['images'][number] & { location: { coords: [number, number, number]; srid: string } })[]
  >([]);

  const layers = useAtomValue(selectedLayersAtom(projectId));
  const allLayers = useAtomValue(layersAtom);
  const pointcloudLayers = useMemo(() => layers.filter((l) => l.type === 'POINTCLOUD'), [layers]);
  const [stockpileMode, setStockpileMode] = useAtom(stockpileToolModeAtom);
  const [pipeMode, setPipeMode] = useAtom(pipeToolModeAtom);
  // PostHog flag for the stockpile-volume tool. Coerce undefined → false so the
  // toolbar button stays hidden until the flag positively enables it. Same
  // pattern as `isGoogleStreetViewEnabled` above.
  const isStockpileAnalysisEnabled = !!useFeatureFlagEnabled('stockpileAnalysis');
  // Shared-link visitors (opened via `?token=`, no normal auth) never get the
  // Elevation Profile or Stockpile tools — same `getLinkSessionId()` signal
  // ViewerPage uses to hide deviation analysis. Gates the toolbar buttons, the
  // in-canvas tools and the off-canvas panels, so a tool-mode atom left
  // non-closed can't surface any of their UI either.
  const isSharedLink = !!getLinkSessionId();
  // PostHog flag for pipe detection — coerce undefined → false so the tool stays
  // hidden until the flag positively enables it (same pattern as stockpile).
  const isPipeAnalysisEnabled = !!useFeatureFlagEnabled('pipe');
  // PostHog flag for crest-line extraction — coerce undefined → false so the
  // preview mesh, draw-guide tool and its toolbar all stay unmounted until the
  // flag positively enables it (same pattern as stockpile/pipe). Orthogonal to
  // the `analysis.crestLine` subscription gate in Layers.tsx: this flag decides
  // whether the feature exists in this deployment at all, the gate decides
  // whether this org's plan can use it.
  const isCrestEnabled = !!useFeatureFlagEnabled('crest');

  // Subscription-tier feature gates.
  // A shared link can always use the clip box: it only changes the view (its
  // export is hidden there), an anonymous visitor has no plan to upgrade, and
  // a saved view can open the link inside one that must be switchable off.
  // The org's billing status would otherwise lock even this free gate.
  const clipBoxFeature = useFeature('viewer.clipBox');
  const clipBoxGate = isSharedLink ? { ...clipBoxFeature, allowed: true } : clipBoxFeature;
  const exportClipBoxGate = useFeature('viewer.exportClipBox');
  const stationVolumesGate = useFeature('analysis.stationVolumes');
  const [exportClipBoxOpen, setExportClipBoxOpen] = useState(false);

  /**
   * The station-volumes dock's "PDF report" items (rulings T10.6 / T15.6,
   * plus the layer-stack compliance report of spec §7). The handler lives in
   * `useOpenAnalysisReport` so the whole path — dock item, paywall, dialog,
   * mutation payload — can be mounted and driven by a test; see that file for
   * why that matters.
   */
  const openStationVolumesReport = useOpenAnalysisReport();

  // Layer options for the ClipBox export dialog (POINTCLOUD layers only)
  const pointCloudLayerOptions = useMemo(
    () => pointcloudLayers.map((l) => ({ id: l.id, name: l.name })),
    [pointcloudLayers],
  );
  const defaultExportLayerId = pointCloudLayerOptions[0]?.id ?? null;

  // Per-layer settings for all pointcloud layers (used to detect active deviation shading)
  const pointcloudLayerSettings = useLayerSettingsMany(pointcloudLayers.map((l) => l.id));

  // VECTOR layers from a RAINFALL_ANALYSIS run. Each one is the user-
  // visible result of one rainfall run; the panels (summary + controls)
  // mount per VECTOR layer, NOT per containing folder, so multiple runs
  // against the same source TIN each get their own panel set.
  //
  // Pairing with the TILE sibling for the raster toggle goes via
  // `metadata.rainfallRunJobId` (set by postprocess on both children).
  const rainfallVectorLayers = useMemo(
    () =>
      layers.filter((l) => {
        if (l.isHidden || l.type !== 'VECTOR') return false;
        const meta = (l.metadata ?? {}) as Record<string, unknown>;
        return meta.type === 'RAINFALL_VECTOR';
      }),
    [layers],
  );

  // RAINFALL_TILE layers that are currently *visible* — each gets a
  // small colour legend pinned next to the controls panels so the user
  // can read upstream-area values off the heatmap. Hidden ones are
  // excluded (no point showing a legend for an invisible raster).
  const visibleRainfallTileLayers = useMemo(
    () =>
      layers.filter((l) => {
        if (l.isHidden || !l.visible || l.type !== 'TILE') return false;
        const meta = (l.metadata ?? {}) as Record<string, unknown>;
        return meta.type === 'RAINFALL_TILE';
      }),
    [layers],
  );

  // Layers that are visible and actively shading by the deviation scalar
  // (source === PointColorType.CUSTOM_SCALAR)
  const deviationLayers = useMemo(
    () =>
      pointcloudLayers.filter((l) => {
        if (l.isHidden) return false;
        const meta = (l.metadata ?? {}) as Record<string, unknown>;
        if (meta.scalarAttribute !== 'Deviation') return false;
        const setting = pointcloudLayerSettings[l.id];
        return setting?.pointCloudMaterialColorSource === PointColorType.CUSTOM_SCALAR;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pointcloudLayers, pointcloudLayerSettings],
  );
  // A clearance run's derived cloud, recognised by the metadata block the
  // postprocess writes — not by `scalarAttribute`, which the user is free to
  // switch away from without the run's results ceasing to exist.
  const clearanceLayers = useMemo(
    () =>
      pointcloudLayers.filter((l) => !l.isHidden && !!((l.metadata ?? {}) as DerivedClearanceLayerMetadata).clearance),
    [pointcloudLayers],
  );
  const ifcModelLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'IFC'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      layers
        .filter((l) => l.type === 'MODEL' && l.metadata?.type === 'IFC')
        .map((l) => l.id)
        .join(','),
    ],
  );
  const landxmlLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'LANDXML'),
    [layers],
  );
  const glbLayers = useMemo(() => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'GLB'), [layers]);
  // GLB layers that are on-screen AND carry surface-difference metadata —
  // these get the cut/fill results overlay in the top-right analysis stack.
  const visibleGlbLayers = useMemo(
    () => glbLayers.filter((l) => !l.isHidden && !!(l.metadata as Record<string, any> | null)?.surfaceDifference),
    [glbLayers],
  );
  const xgridsLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'XGRIDS'),
    [layers],
  );
  const splatLayers = useMemo(() => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'SPLAT'), [layers]);
  const fragmentLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'FRAGMENT'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      layers
        .filter((l) => l.type === 'MODEL' && l.metadata?.type === 'FRAGMENT')
        .map((l) => `${l.id}:${JSON.stringify((l.metadata as { georef?: unknown } | null)?.georef ?? null)}`)
        .join(','),
    ],
  );

  const { data: imagesData } = useQuery({
    queryKey: [
      'images',
      layers
        .filter((l) => l.type === 'IMAGE')
        .map((l) => l.id)
        .join(','),
    ],
    queryFn: () =>
      loadImagesByLayerOfflineAware(
        layers.filter((l) => l.type === 'IMAGE').map((l) => l.id),
        projectId,
      ),
  });

  const vectorLayers = useMemo(() => layers.filter((layer) => layer.type === 'VECTOR'), [layers]);

  const features = useLayerFeatures(
    vectorLayers.map((layer) => layer.id),
    projectId,
  );
  const mapOrigin = useStableMapOrigin(features);

  /** `#canvas-container` — the region the movable windows live in (spec §12.1). */
  const canvasRegionRef = useRef<HTMLDivElement | null>(null);

  // R3F root state, captured in the Canvas's `onCreated`. The canvas runs
  // without `preserveDrawingBuffer` (it costs an extra full-resolution buffer,
  // which matters on mobile), so the drawing buffer is cleared after each
  // composite — the screenshot renders a frame itself and reads it back in
  // the same task.
  const r3fStateRef = useRef<RootState | null>(null);

  const _handleScreenshot = useCallback(() => {
    const r3f = r3fStateRef.current;
    if (!r3f) return;
    r3f.gl.render(r3f.scene, r3f.camera);
    const dataUrl = r3f.gl.domElement.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `screenshot-${Date.now()}.png`;
    a.click();
  }, []);

  const _handleToggleMeasurements = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('showMeasurements', isMeasurementsEnabled ? 'false' : 'true');
    if (!isMeasurementsEnabled) params.set('showAreaMeasurement', 'false');
    analytics.capture(isMeasurementsEnabled ? 'Project Measurements Disabled' : 'Project Measurements Enabled');
    setParams(params);
  };

  const _handleToggleElevationProfile = () => {
    // Toolbar toggle:
    //   closed  → start a fresh drawing session
    //   drawing → cancel the in-progress polyline
    //   panel   → close the panel (and any active draft view)
    if (elevationProfileMode.phase === 'closed') {
      setElevationProfileMode({ phase: 'drawing', activeId: null });
    } else {
      setElevationProfileMode({ ...DEFAULT_ELEVATION_PROFILE_TOOL_MODE });
    }
  };

  const _handleToggleAreaMeasurement = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('showAreaMeasurement', isAreaMeasurementEnabled ? 'false' : 'true');
    if (!isAreaMeasurementEnabled) params.set('showMeasurements', 'false');
    analytics.capture(
      isAreaMeasurementEnabled ? 'Project Area Measurement Disabled' : 'Project Area Measurement Enabled',
    );
    setParams(params);
  };

  const images = useMemo(() => imagesData ?? [], [imagesData]);

  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const showFps = useAtomValue(showFpsAtom);
  const isStaff = isInternalUser(useAtomValue(userAtom)?.email);

  // Seed the viewer-state atom's position from the project's initial position
  // on first mount (atom still at the (0,0,0) default). Runs as a layout
  // effect so the atom update commits before Controls' position-sync
  // useEffect fires — otherwise Controls would lock onto (0,0,0) on the
  // first sync and the flying-mode early-return below would block recovery.
  useLayoutEffect(() => {
    if (!projectData?.project?.initialPosition) return;
    setProjectInitialPosition(projectId, projectData.project.initialPosition);
    updateViewerStateWithInitialPosition(projectId, setViewerState);
  }, [projectData?.project?.initialPosition, projectId, setViewerState]);

  // Parsed initial position passed straight into Controls so its first
  // position-sync uses the project initial position even if the atom-driven
  // `position` prop hasn't yet caught up across the DOM ↔ R3F root boundary.
  const projectInitialPos = useMemo(() => {
    const ip = projectData?.project?.initialPosition;
    if (!ip || !ip.coords) return null;
    const [x, y, z] = ip.coords;
    return { x, y, z: z ?? 0 };
  }, [projectData?.project?.initialPosition]);

  // Auto-select base map: project first, then user, then Mapbox (null).
  // Also clear stale activeBaseMapId if the referenced base map was deleted.
  useEffect(() => {
    const projectBMs = (projectData?.project?.baseMaps ?? []) as CustomBaseMap[];
    const userBMs = (viewerSettings?.customBaseMaps ?? []) as CustomBaseMap[];
    const allBaseMaps = [...projectBMs, ...userBMs];

    if (viewerState.activeBaseMapId) {
      // Validate existing selection still exists
      if (!allBaseMaps.find((bm) => bm.id === viewerState.activeBaseMapId)) {
        // Deleted — fall through to auto-select logic below
        const autoId = projectBMs[0]?.id ?? userBMs[0]?.id ?? null;
        setViewerState((prev) => ({ ...prev, activeBaseMapId: autoId }));
      }
      return;
    }

    // No active base map — auto-select: project > user > null (Mapbox)
    const autoId = projectBMs[0]?.id ?? userBMs[0]?.id ?? null;
    if (autoId) {
      setViewerState((prev) => ({ ...prev, activeBaseMapId: autoId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData?.project?.baseMaps, viewerSettings?.customBaseMaps]);
  const position = useMemo(
    () => new THREE.Vector3(viewerState.position.x, viewerState.position.y, viewerState.position.z),
    [viewerState.position.x, viewerState.position.y, viewerState.position.z],
  );

  const setPosition = useCallback(
    (value: SetStateAction<THREE.Vector3>) => {
      setViewerState((prev) => {
        const current = new THREE.Vector3(prev.position.x, prev.position.y, prev.position.z);
        const next = typeof value === 'function' ? value(current) : value;

        return {
          ...prev,
          position: {
            x: next.x ?? DEFAULT_VIEWER_POSITION.x,
            y: next.y ?? DEFAULT_VIEWER_POSITION.y,
            z: next.z ?? DEFAULT_VIEWER_POSITION.z,
          },
        };
      });
    },
    [setViewerState],
  );

  const controlRef = useRef<CameraControls | null>(null);
  const cameraControlsRef = useRef<CameraControlsImpl | null>(null);
  const skipPositionSyncRef = useRef(false);
  const cameraRef = useRef<Camera>(null);
  const { imageId, googleImageId } = viewerState;
  const setImageId = useCallback(
    (value: SetStateAction<string | null>) => {
      setViewerState((prev) => ({
        ...prev,
        imageId: typeof value === 'function' ? value(prev.imageId) : value,
      }));
    },
    [setViewerState],
  );

  const setGoogleImageId = useCallback(
    (value: SetStateAction<string | null>) => {
      setViewerState((prev) => ({
        ...prev,
        googleImageId: typeof value === 'function' ? value(prev.googleImageId) : value,
      }));
    },
    [setViewerState],
  );
  const [hoverImageId, setHoverImageId] = useAtom(hoverImageIdAtom(projectId));

  useEffect(() => {
    if (!projectData?.project.srid || projectData.project.srid === -1) return;
    (async () => {
      project([position.x, position.y, position.z], `EPSG:${projectData?.project.srid}`).then((coords) =>
        setPositionWGS84(new THREE.Vector3(...coords)),
      );
    })();
  }, [position.x, position.y, position.z, projectData?.project.srid]);

  useEffect(() => {
    if (!projectData) return;
    if (!googleImagesData?.images) return setGoogleImages([]);

    Promise.all(
      googleImagesData.images.map(async (image) => {
        return {
          ...image,
          location: {
            coords: await project(
              [image.locationWGS84.coords[0], image.locationWGS84.coords[1], image.locationWGS84.coords[2]],
              'EPSG:4326',
              `EPSG:${projectData.project.srid}`,
            ),
            srid: `EPSG:${projectData.project.srid}`,
          },
        };
      }),
    ).then((images) => setGoogleImages(images));
  }, [googleImagesData?.images]);

  const imagePositions = useMemo(() => {
    if (!images) return null;

    function getCamerasCentroid(imageList: typeof images) {
      const centroid = new THREE.Vector3();
      for (const img of imageList) {
        centroid.add(new THREE.Vector3(img.location.coords[0], img.location.coords[1], img.location.coords[2]));
      }

      centroid.divideScalar(imageList.length);

      return centroid;
    }

    const allPositions = groupImagesByPosition2(images);
    return allPositions.map((imagePosition) => ({
      ...imagePosition,
      display: viewerSettings.showAllImagePOIs
        ? true
        : new THREE.Vector3(...imagePosition.xyz).distanceTo(position) <= (viewerSettings.imagePOIMaxDistance ?? 50),
      cameraPosition: getCamerasCentroid(imagePosition.entries),
      renderPosition: getCamerasCentroid(imagePosition.entries).sub(new THREE.Vector3(0, 0, 1.5)),
    }));
  }, [images, position, viewerSettings.imagePOIMaxDistance, viewerSettings.showAllImagePOIs]);

  useEffect(() => {
    // Wait until images have loaded before deciding anything. Nulling out
    // imageId here while the query is still in flight wipes the preserved
    // selection from a previous visit, which then makes the closest-image
    // search below kick in and pick the wrong image.
    if (!imagePositions || imagePositions.length === 0) return;
    const imagePosition = imagePositions.find((p) => p.entries[0].id === imageId);

    if (imagePosition) return;

    // Find the closest image to the atom-tracked viewer position. We can't
    // use `cameraRef.current.position` here because on a Canvas remount the
    // ref is briefly null until Controls' first sync runs, and the cached
    // images query can fire this effect before that — falling back to
    // Infinity then deterministically picks `imagePositions[0]` regardless
    // of where the user actually was.
    let closestImagePosition = imagePositions[0];
    let closestDistance = Infinity;

    imagePositions.forEach((imagePosition) => {
      const distance = position.distanceTo(new THREE.Vector3(...imagePosition.xyz));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestImagePosition = imagePosition;
      }
    });

    setImageId(closestImagePosition.entries[0].id);
  }, [position, imagePositions, imageId, setImageId]);

  const isFlying = (layers.every((l) => l.type !== 'IMAGE') || !showImages) && !showGoogleStreetView;
  // const isFlying = false;

  // The 3rd-person / orthographic camera toggles only make sense in flying
  // mode (i.e. when no IMAGE layer is shown). To let the user switch into
  // flying mode straight from an image view, the camera buttons stay visible
  // even while images are on; clicking one first deselects the IMAGE layers
  // (which flips `isFlying` true) and then applies the camera change.
  const setSelectedLayerIds = useSetAtom(selectedLayerIdsAtom(projectId));
  const deselectImageLayers = useCallback(() => {
    const imageLayerIds = new Set(layers.filter((l) => l.type === 'IMAGE').map((l) => l.id));
    if (imageLayerIds.size === 0) return;
    setSelectedLayerIds((ids) => ids.filter((id) => !imageLayerIds.has(id)));
  }, [layers, setSelectedLayerIds]);

  /**
   * The one way the camera mode changes: the switch, the hotkeys and the
   * surface-draw toolbar all go through here. Dropping the IMAGE layers first
   * is what flips `isFlying` true, so a camera mode can be picked straight out
   * of an image view.
   */
  const setCameraMode = useCallback(
    (next: CameraMode) => {
      deselectImageLayers();
      if (cameraMode !== 'ORTHO') previousPerspectiveRef.current = cameraMode;
      setCameraModeState(next);
    },
    [cameraMode, deselectImageLayers],
  );

  // Ortho and the image (pano) view are mutually exclusive: a pano is viewed
  // from inside, and an ortho camera shows it as a flat disc. `setCameraMode`
  // keeps that when the camera changes; this keeps it when the layers change.
  // On restore (a reload resumed ortho with the images back on) the camera
  // wins and the images go off; after that, switching an image layer on
  // leaves ortho for the perspective mode the user came from.
  const imageLayersOn = showImages && layers.some((l) => l.type === 'IMAGE');
  const orthoImagesSettledRef = useRef(false);
  useEffect(() => {
    if (!orthoCamera || !imageLayersOn) {
      if (layers.length > 0) orthoImagesSettledRef.current = true;
      return;
    }
    if (!orthoImagesSettledRef.current) {
      orthoImagesSettledRef.current = true;
      deselectImageLayers();
      return;
    }
    setCameraModeState(previousPerspectiveRef.current);
  }, [orthoCamera, imageLayersOn, layers.length, deselectImageLayers]);

  // Saved-views capture/apply (shared with the Layer Manager's Saved Views section).
  const { applyView } = useViewCaptureApply(projectId);

  // Saved-views: a view apply (from the Layer Manager section or the deep-link
  // below) emits `applyCameraState`. Set the camera-mode flags from it here;
  // the in-Canvas Controls consumer performs the actual camera transform once
  // the projection type matches.
  useEffect(() => {
    return viewerEvents.on('applyCameraState', (cam) => {
      setCameraModeState(cameraModeFromFlags(cam));
    });
  }, []);

  // Saved view on project open. Precedence:
  //   1. `?view=<id>` deep link (copied link / shared URL);
  //   2. the session-restored camera (`storedCameraState`: the user already
  //      navigated this project in this tab, e.g. a reload) — nothing applied;
  //   3. the project's initial saved view (`isInitial`);
  //   4. otherwise the project's initialPosition/initialAngle (Controls).
  // One-shot per mount; the deep link is decided first so an unknown id still
  // blocks the initial view rather than racing it.
  const { data: savedViewsData } = useSavedViews(projectId);
  const appliedDeepLinkRef = useRef(false);

  useEffect(() => {
    if (appliedDeepLinkRef.current) return;
    if (allLayers.length === 0) return; // wait for layers so visibility filter is correct
    if (!savedViewsData) return;
    const viewId = params.get('view');

    if (viewId) {
      const view = savedViewsData.savedViews.find((v) => v.id === viewId);
      if (!view) return;
      appliedDeepLinkRef.current = true;
      applyView(view);

      const next = new URLSearchParams(params);
      next.delete('view');
      setParams(next, { replace: true });
      return;
    }

    // Wait for the project too: its `defaultThirdPersonView` effect (above)
    // sets the camera mode once project data lands, and the view's own mode
    // must be applied after it, not before.
    if (!projectData?.project) return;
    appliedDeepLinkRef.current = true;
    if (storedCameraState) return;
    const initialView = savedViewsData.savedViews.find((v) => v.isInitial);
    if (initialView) applyView(initialView);
  }, [params, savedViewsData, allLayers.length, applyView, setParams, storedCameraState, projectData?.project]);

  /**
   * `[` / `]` while the station-volumes dock is open (Shift: ×10).
   *
   * `stepStation` is the walk toolbar's own pure step helper: in snap mode it
   * advances to the next alignment key point (landing on the start/end when
   * there is none further in that direction), otherwise it moves
   * `walk.step * mult` metres and clamps to `[0, length]`. It knows nothing
   * about bins — what makes a press land on the neighbouring bin is that the
   * dock seeds `walk.step` from the run's `stationIntervalM` when it opens. If
   * the user changes the step on the toolbar, or turns snapping on, these keys
   * follow that instead, which is the behaviour the toolbar's own `[` / `]`
   * had before the dock took the keys over.
   *
   * Read through the store rather than `useAtomValue`: `Viewer` renders the
   * whole R3F tree, and subscribing it to the walk cursor would re-render that
   * tree on every step (and on every camera-follow update).
   */
  const store = useStore();
  const stationDockOpen = useAtomValue(stationDockOpenAtom);
  const clearanceDockOpen = useAtomValue(clearanceDockOpenAtom);
  const stepStationBin = useCallback(
    (dir: 1 | -1, mult: number) => {
      const walk = store.get(alignmentWalkAtom);
      if (!walk) return;
      const alignment = store.get(alignmentsAtom).get(walk.key);
      if (!alignment) return;
      store.set(alignmentWalkAtom, { ...walk, station: stepStation(alignment, walk, dir, mult) });
    },
    [store],
  );
  const onStationPrev = useCallback((mult: number) => stepStationBin(-1, mult), [stepStationBin]);
  const onStationNext = useCallback((mult: number) => stepStationBin(1, mult), [stepStationBin]);

  // The top-right analysis column is only mounted when it has something to
  // show. Nothing else keys off it any more: the minimap, the walk toolbar and
  // the column are all movable windows now (spec §12.2), each with a default
  // slot that clears the other two, so none of them has to reserve a band for
  // chrome that may or may not be on screen.
  // Through `analysisColumnHasContent` rather than an inline `||` chain: this
  // condition is the ONLY mount point for every analysis chip, and the inline
  // version silently shipped the clearance feature with no entry point at all
  // (review H1). The exact-record parameter makes the same omission a type
  // error for the next analysis.
  const analysisColumnOpen = analysisColumnHasContent({
    rainfall: rainfallVectorLayers.length,
    deviation: deviationLayers.length,
    surfaceDifference: visibleGlbLayers.length,
    clearance: clearanceLayers.length,
  });
  // The toolbar toggle that hides the analysis UI only shows up when there is
  // analysis UI to hide: the column or a rainfall heatmap legend.
  const hasAnalysisPanels = analysisColumnOpen || visibleRainfallTileLayers.length > 0;
  // Compare surfaces (spec §4.4): when both halves of a paired run are on
  // screen, their panels stack with Cut & fill first — the cut & fill card
  // moves to directly above its deviation card. Every other cut & fill card
  // keeps its place at the end of the column.
  const { leadBy: cutFillLeadBy, rest: cutFillRest } = stackCutFillFirst(deviationLayers, visibleGlbLayers);
  const renderCutFillPanel = (l: (typeof visibleGlbLayers)[number]) => {
    const meta = (l.metadata ?? {}) as Record<string, any>;
    return (
      <SurfaceDifferencePanel
        key={l.id}
        projectId={projectId}
        layerId={l.id}
        layerName={l.name}
        meta={meta.surfaceDifference}
        stationVolumes={meta.stationVolumes}
        batchId={meta.batchId}
        comparison={comparisonReportTarget(l, layers)}
      />
    );
  };

  // Register viewer keyboard shortcuts
  useViewerHotkeys({
    onToggleMap: () => setShowMap((prev) => !prev),
    onTogglePointcloud: () => setHidePointcloud((prev) => !prev),
    onToggleClipBox: () => setClipBoxState((prev) => ({ ...prev, enabled: !prev.enabled })),
    onToggleOrthoCamera: () => setCameraMode(toggleOrtho(cameraMode, previousPerspectiveRef.current)),
    onToggleThirdPerson: () => setCameraMode(togglePerson(cameraMode)),
    onToggleStreetView: () => setShowGoogleStreetView(!showGoogleStreetView),
    onToggleMeasureDistance: _handleToggleMeasurements,
    onToggleMeasureArea: _handleToggleAreaMeasurement,
    onScreenshot: _handleScreenshot,
    onToggleAnalysisPanels: () => setHideAnalysisPanels((prev) => !prev),
    hasAnalysisPanels,
    hasPointclouds: pointcloudLayers.length > 0,
    hasMeasureableTargets:
      pointcloudLayers.length > 0 || ifcModelLayers.length > 0 || fragmentLayers.length > 0 || landxmlLayers.length > 0,
    isFlying,
    canToggleCameraMode: !showGoogleStreetView,
    isGoogleStreetViewEnabled: !!isGoogleStreetViewEnabled,
    canUseClipBox: clipBoxGate.allowed,
    onStationPrev,
    onStationNext,
    // `stationDockOpenAtom` is true for the CLEARANCE dock as well (ruling R2:
    // one dock atom, one `dataset` discriminator), so these `[` / `]` hotkeys
    // are live over a clearance dock while being gated on the station-volumes
    // key. Both keys are `minProduct: 'PRO'` today so nothing is reachable that
    // should not be; split the gate here the moment that stops being true.
    hasStationVolumes: stationDockOpen,
    canUseStationVolumes: stationVolumesGate.allowed,
    // Shift+[ / Shift+] walk to the previous / next violating interval while
    // the clearance dock is open — ungated, like the dock's own chevrons.
    hasClearanceDock: clearanceDockOpen,
  });

  // The corner column (gizmo, scale bar, coordinates) stays in the bottom-right
  // corner. With the toolbar along the bottom it is narrowed to the room left
  // beside the bar and the coordinates wrap to fit; it only lifts above the bar
  // when even that room is gone. Lifting on overlap instead made it jump up and
  // down as the coordinate digits changed (ortho's Z is ~1000 m, one digit
  // wider). Measured, because the widths depend on content and on the layer
  // sidebar.
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const bottomRightRef = useRef<HTMLDivElement | null>(null);
  const [bottomRightRoom, setBottomRightRoom] = useState<{ lift: boolean; maxWidth: number | null }>({
    lift: false,
    maxWidth: null,
  });
  const toolbarAtBottom = viewerSettings.actionPosition !== 'RIGHT' || isNarrow;
  const hasProjectData = !!projectData;
  useLayoutEffect(() => {
    const bar = toolbarRef.current;
    const corner = bottomRightRef.current;
    if (!toolbarAtBottom || !bar || !corner) {
      setBottomRightRoom({ lift: false, maxWidth: null });
      return;
    }
    const check = () => {
      // Both edges are fixed (left-2 bar, right-2 column), so narrowing the
      // column cannot feed back into the measurement.
      const room = Math.floor(corner.getBoundingClientRect().right - bar.getBoundingClientRect().right - 8);
      const next = room >= 96 ? { lift: false, maxWidth: room } : { lift: true, maxWidth: null };
      setBottomRightRoom((prev) => (prev.lift === next.lift && prev.maxWidth === next.maxWidth ? prev : next));
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(bar);
    if (canvasRegionRef.current) observer.observe(canvasRegionRef.current);
    // The layer sidebar can slide the canvas edge without a final resize
    // notification; re-measure once any transition settles.
    window.addEventListener('transitionend', check, true);
    window.addEventListener('resize', check);
    return () => {
      observer.disconnect();
      window.removeEventListener('transitionend', check, true);
      window.removeEventListener('resize', check);
    };
  }, [toolbarAtBottom, hasProjectData]);

  const googleImage = useMemo(
    () => googleImages.find((img) => img.id === googleImageId) || null,
    // hacky solution to circumvent url update in googleImage causing texture loader to load it twice
    [googleImageId, googleImages.some((img) => img.id === googleImageId)],
  );

  if (!projectData) return null;

  // A right-hand column does not fit a phone; the narrow layout is always at the bottom.
  const toolbarOnRight = viewerSettings.actionPosition === 'RIGHT' && !isNarrow;

  // Rendered as the icon bar on wide screens and as the rows of the Tools
  // sheet on narrow ones (`ToggleIconButton` reads `ToolbarSheetContext`).
  const toolButtons = (
    <>
      {!hideMap && (
        <ToggleIconButton
          active={showMap}
          icon={<Map />}
          tooltip={showMap ? t('viewer3d.hideMap') : t('viewer3d.showMap')}
          onClick={() => setShowMap(!showMap)}
          shortcutKey='M'
        />
      )}
      <ToggleIconButton
        icon={<CameraSolid />}
        tooltip={t('viewer3d.screenshot')}
        onClick={_handleScreenshot}
        shortcutKey='I'
      />
      {isGoogleStreetViewEnabled && (
        <ToggleIconButton
          active={showGoogleStreetView}
          icon={<AccessibilitySolid />}
          tooltip={showGoogleStreetView ? t('viewer3d.hideStreetView') : t('viewer3d.showStreetView')}
          onClick={() => setShowGoogleStreetView(!showGoogleStreetView)}
          shortcutKey='G'
        />
      )}
      {(pointcloudLayers?.length > 0 ||
        ifcModelLayers?.length > 0 ||
        fragmentLayers?.length > 0 ||
        landxmlLayers?.length > 0) && (
        <>
          <ToggleIconButton
            active={isMeasurementsEnabled}
            icon={<RulerSolid />}
            tooltip={isMeasurementsEnabled ? t('viewer3d.stopMeasuring') : t('viewer3d.measureDistance')}
            onClick={() => _handleToggleMeasurements()}
            shortcutKey='R'
          />
          <ToggleIconButton
            active={isAreaMeasurementEnabled}
            icon={<BoundingBoxSolid />}
            tooltip={isAreaMeasurementEnabled ? t('viewer3d.stopAreaMeasurement') : t('viewer3d.measureArea')}
            onClick={() => _handleToggleAreaMeasurement()}
            shortcutKey='B'
          />
          {!isSharedLink && pointcloudLayers?.length > 0 && (
            <FeatureGate feature='viewer.elevationProfile'>
              <ToggleIconButton
                active={elevationProfileMode.phase !== 'closed'}
                icon={<ChartLineSolid />}
                tooltip={
                  elevationProfileMode.phase === 'drawing'
                    ? t('elevationProfile.toolbarStop')
                    : t('elevationProfile.toolbarStart')
                }
                onClick={_handleToggleElevationProfile}
              />
            </FeatureGate>
          )}
        </>
      )}
      {isStockpileAnalysisEnabled && !isSharedLink && pointcloudLayers?.length > 0 && (
        <FeatureGate feature='viewer.stockpile'>
          <ToggleIconButton
            active={stockpileMode.enabled}
            icon={<MountainSolid />}
            tooltip={stockpileMode.enabled ? t('viewer3d.stockpileStop') : t('viewer3d.stockpileStart')}
            onClick={() =>
              setStockpileMode((prev) =>
                prev.enabled ? DEFAULT_STOCKPILE_TOOL_MODE : { ...DEFAULT_STOCKPILE_TOOL_MODE, enabled: true },
              )
            }
          />
        </FeatureGate>
      )}
      {/* The pipe tool saves layers, which a shared link can't. */}
      {isPipeAnalysisEnabled && !isSharedLink && pointcloudLayers?.length > 0 && (
        <FeatureGate feature='viewer.pipe'>
          <ToggleIconButton
            active={pipeMode.enabled}
            icon={<GitMergeSolid />}
            tooltip={pipeMode.enabled ? t('pipe.toolbarStop') : t('pipe.toolbarStart')}
            onClick={() =>
              setPipeMode((prev) =>
                prev.enabled
                  ? DEFAULT_PIPE_TOOL_MODE
                  : { ...DEFAULT_PIPE_TOOL_MODE, enabled: true, phase: 'seeking' },
              )
            }
          />
        </FeatureGate>
      )}
      {hasAnalysisPanels && (
        <ToggleIconButton
          active={hideAnalysisPanels}
          icon={<PanelRightCloseSolid />}
          tooltip={hideAnalysisPanels ? t('viewer3d.showAnalysisPanels') : t('viewer3d.hideAnalysisPanels')}
          onClick={() => setHideAnalysisPanels(!hideAnalysisPanels)}
          shortcutKey='H'
        />
      )}
      {pointcloudLayers?.length > 0 && (
        <ToggleIconButton
          active={hidePointcloud}
          icon={<EyeSlashSolid />}
          tooltip={hidePointcloud ? t('viewer3d.showPointcloud') : t('viewer3d.hidePointcloud')}
          onClick={() => setHidePointcloud(!hidePointcloud)}
          shortcutKey='P'
        />
      )}
      <ToggleIconButton
        active={clipBoxEnabled && clipBoxGate.allowed}
        locked={!clipBoxGate.allowed}
        icon={<BoxSolid />}
        tooltip={
          clipBoxGate.allowed
            ? clipBoxEnabled
              ? t('viewer3d.removeClipBox')
              : t('viewer3d.clipBox')
            : tFg('tooltip.requires', {
                requirement:
                  clipBoxGate.gate.minProduct === 'PRO'
                    ? tFg('requirement.pro')
                    : clipBoxGate.gate.minStorageBytes != null &&
                        clipBoxGate.gate.minStorageBytes >= 1_000_000_000_000
                      ? tFg('requirement.business')
                      : clipBoxGate.gate.minStorageBytes != null
                        ? tFg('requirement.team')
                        : tFg('requirement.paid'),
              })
        }
        onClick={
          clipBoxGate.allowed
            ? () => setClipBoxState((prev) => ({ ...prev, enabled: !prev.enabled }))
            : clipBoxGate.promptUpgrade
        }
        shortcutKey={clipBoxGate.allowed ? 'C' : undefined}
      />
    </>
  );

  return (
    // The viewer is a COLUMN: one region for the canvas and all of its
    // overlays, and the station-volumes dock as a sibling below it. The dock
    // used to be an `absolute bottom-0` overlay and every bottom-anchored
    // widget (tool rail, coordinate box, joystick, gizmo, analysis column)
    // had to lift itself by the dock's height — which pushed them into the
    // middle of the canvas and into each other. As a layout row the dock
    // takes its own space: the region shrinks, R3F's ResizeObserver resizes
    // the canvas, and nothing else has to know the dock exists.
    <div className='absolute inset-0 z-10 flex flex-col overflow-hidden'>
      <div className='relative flex min-h-0 flex-1 overflow-hidden'>
        <ViewerLoadingColumn />
        <QuickUploadDropZone projectId={projectId} />
        <div
          id='canvas-container'
          // Spec §12.1's `bounds`: the movable windows are confined to the
          // canvas region, which is the flex sibling ABOVE the station-volumes
          // dock — so a window can never be dragged over the dock.
          ref={canvasRegionRef}
          tabIndex={0}
          style={viewerSettings.backgroundColor ? { backgroundColor: viewerSettings.backgroundColor } : undefined}
          className={
            viewerSettings.backgroundColor
              ? 'overflow-hidden flex-1 min-w-0 relative'
              : 'overflow-hidden flex-1 min-w-0 relative bg-slate-100/70 dark:bg-stone-900/70'
          }
        >
          <Suspense fallback={null}>
            {showMap && !hideMap && (
              <MinimapWindow boundsRef={canvasRegionRef}>
                <MapView projectId={projectId} mode='overlay' boundsRef={canvasRegionRef} />
              </MinimapWindow>
            )}
          </Suspense>
          <Canvas
            // Force a fresh R3F root + Three.js scene on every project switch.
            // The DOM-level ErrorBoundary key={projectId} doesn't propagate
            // remount semantics into R3F's internal root, so without this key
            // Controls / cameraRef / storedCameraRef survive the switch and
            // restore the previous project's camera state.
            key={projectId}
            // @ts-ignore
            raycaster={{ params: { Line: { threshold: 0.1 }, Points: { threshold: 0.01 } } }}
            camera={{ fov: 60, near: 0.01, far: 10_000 }}
            gl={{ alpha: true }}
            onCreated={(state) => {
              r3fStateRef.current = state;
            }}
          >
            <SceneBackground color={viewerSettings.backgroundColor ?? null} />
            {(showFps && isStaff) ||
            (!isTauri() &&
              (window.location.hostname === 'localhost' || window.location.hostname === 'local.skygis.app')) ? (
              <Stats />
            ) : null}
            <OrthographicCamera
              near={0.000001}
              far={10_000}
              // zoom={10}
              zoom={10}
              makeDefault={orthoCamera}
            />
            <Controls
              projectId={projectId}
              cameraRef={cameraRef}
              cameraControlsRef={cameraControlsRef}
              skipPositionSyncRef={skipPositionSyncRef}
              position={position}
              setPosition={setPosition}
              flying={isFlying}
              thirdperson={isThirdPerson(cameraMode)}
              cameraMode={cameraMode}
              isTouchDevice={isTouchDevice}
              initialAngle={projectData.project.initialAngle}
              initialPosition={projectInitialPos}
            />
            <group position={position}>
              <directionalLight position={new THREE.Vector3(10, 10, 0)} intensity={0.3} color='white' castShadow />
            </group>
            <hemisphereLight color={0xeeeeff} groundColor={0x777788} intensity={0.2} castShadow />
            <ambientLight intensity={0.3} castShadow />
            {showGoogleStreetView ? (
              <Suspense fallback={<Loader />}>
                <GoogleStreetView
                  imageId={googleImageId}
                  googleImage={googleImage}
                  setImageId={setGoogleImageId}
                  setPosition={setPosition}
                  position={position}
                  projection={`EPSG:${projectData.project.srid}`}
                  hoverImageId={hoverImageId}
                  setHoverImageId={setHoverImageId}
                  googleImages={googleImages}
                  positionWGS84={positionWGS84}
                />
              </Suspense>
            ) : null}
            {showGoogleStreetView ? null : (
              <>
                <Suspense fallback={null}>
                  <PointCloud
                    visible={!hidePointcloud}
                    layers={pointcloudLayers}
                    controlRef={controlRef}
                    project={projectData.project}
                    features={features}
                  />
                </Suspense>
                <GeoJson
                  features={features}
                  flying={isFlying}
                  layers={vectorLayers}
                  viewerPosition={position}
                  mapOrigin={mapOrigin}
                />
                <RainfallOverflowArrows3D features={features} layers={vectorLayers} mapOrigin={mapOrigin} />
                <RainfallPondFillAnimation3D features={features} layers={vectorLayers} mapOrigin={mapOrigin} />
                {/* Shared lighting for IFC and LandXML models */}
                <directionalLight position={[1, 0.5, 2]} intensity={1.2} color='white' />
                <directionalLight position={[-0.5, -0.5, 0.5]} intensity={0.3} color={0x8899aa} />
                <ambientLight intensity={0.15} />
                <IFC layers={ifcModelLayers} />
                <IFC2 layers={fragmentLayers} />
                <LandXML layers={landxmlLayers} />
                {/* drei <Text> suspends while troika loads its font. Without a
                    boundary here the first station label — the first time the
                    dock opens — would fall through to the Canvas root and blank
                    the whole scene for a moment. */}
                <Suspense fallback={null}>
                  <AlignmentOverlay />
                </Suspense>
                <SwathPreview />
                {/* Both gated on the `crest` PostHog flag: with it off, neither
                    the saved-crest preview mesh nor the draw-guide picking tool
                    mount, so there is nothing to render or interact with even
                    if a project already has saved crest lines from before the
                    flag was disabled. */}
                {isCrestEnabled && (
                  <>
                    <CrestLinePreview />
                    <CrestGuideTool />
                  </>
                )}
                {/* The dialog's live preview while it is open (same lifecycle
                    as the swath preview: an atom the run dialog writes while
                    it is open and clears on unmount), otherwise a finished
                    run's own snapshot — see `ClearanceEnvelope`'s docstring.
                    It also carries the clearance verdict, selection and hover
                    per station bin; there is no separate clearance band mesh
                    any more, because the plates sat in the envelope's own
                    floor plane and read as its bottom. */}
                <ClearanceEnvelope />
                <StationBands />
                {/* After the bands on purpose: the section is drawn over the
                    corridor it belongs to (spec §11.2). */}
                <StationSectionOverlay />
                <GLBModel layers={glbLayers} />
                <Xgrids layers={xgridsLayers} />
                {/* Spark (~2.5 MB incl. inlined wasm) lives in its own chunk; only
                    fetch it once a splat layer is actually shown. */}
                {splatLayers.length > 0 && <LazySplat layers={splatLayers} />}
                <SceneContextMenuPicker />
                <MeasuringTool projectId={projectData.project.id} features={features} />
                <AreaMeasuringTool projectId={projectData.project.id} features={features} />
                {!isSharedLink && (
                  <>
                    <ElevationProfileTool projectId={projectData.project.id} features={features} />
                    <SavedElevationProfiles projectId={projectData.project.id} />
                  </>
                )}
                <Suspense fallback={null}>
                  <SavedMeasurements projectId={projectData.project.id} />
                </Suspense>
                <ClipBoxTool projectId={projectData.project.id} />
                <AddSurfaceTool features={features} />
                <AddLineTool features={features} />
                <AddPolygonTool features={features} />
                {isPipeAnalysisEnabled && (
                  <>
                    <PipeTool projectId={projectData.project.id} />
                    <SavedPipes features={features} />
                  </>
                )}
                <SnapIndicator />
                {/* Every stockpile-related 3-D component is gated on the
                  PostHog flag. The toolbar button up top uses the same gate;
                  this pair below makes sure flag-off users also don't see
                  saved-stockpile labels or the height-column visualization
                  even if the project already contains stockpile features
                  from before the flag was disabled. */}
                {isStockpileAnalysisEnabled && (
                  <>
                    {!isSharedLink && (
                      <>
                        <StockpileTool />
                        <StockpileVisualization />
                      </>
                    )}
                    <SavedStockpiles features={features} />
                  </>
                )}
                {showImages && (
                  <IPRJ
                    setPosition={setPosition}
                    // there's a bug, imagesLayer is sometimes undefined
                    position={position}
                    images={images}
                    imagePositions={imagePositions}
                    layers={layers.filter((l) => l.type === 'IMAGE')}
                    imageId={imageId}
                    setImageId={setImageId}
                    hoverImageId={hoverImageId}
                    setHoverImageId={setHoverImageId}
                  />
                )}
              </>
            )}
          </Canvas>
          <OrthoGrid projectId={projectId} />
          <AddSurfaceToolbar
            projectId={projectData.project.id}
            srid={projectData.project.srid}
            orthoCamera={orthoCamera}
            setOrthoCamera={(next) => setCameraMode(next ? 'ORTHO' : previousPerspectiveRef.current)}
            cameraControlsRef={cameraControlsRef}
            setPosition={setPosition}
            skipPositionSyncRef={skipPositionSyncRef}
          />
          <AddPointToolbar />
          <AddLineToolbar srid={projectData.project.srid} />
          {/* Crest-line "Draw centreline" sketch toolbar. A sibling of the
              Canvas, like every other FloatingToolbar — it renders plain DOM,
              which R3F cannot host. It renders itself only while draw mode is
              on, and draw mode can only be reached through the context-menu
              item in Layers.tsx (behind both the `crest` flag and the
              `analysis.crestLine` subscription gate) — so this is belt and
              braces, matching how `StockpileToolbar` below is gated even
              though it's equally unreachable with its flag off. */}
          {isCrestEnabled && <CrestGuideToolbar />}
          {/* The toolbar is a movable window now (spec §12.2). The band it used
              to be centred in — everything between the minimap's right edge and
              the analysis column's left edge — is gone with it: the window's
              default slot already clears both, and once the user has moved any
              of the three, guessing where the others are is worse than useless.
              Stacking order comes from the windows' own z band, so the old
              `z-[60]`-over-the-minimap's-`z-50` fudge is gone too. The toolbar's
              own internals are untouched; the grip is supplied by the window. */}
          <WalkToolbarWindow boundsRef={canvasRegionRef}>
            <AlignmentWalkToolbar projectId={projectData.project.id} clipBoxGate={clipBoxGate} />
          </WalkToolbarWindow>
          <AddPolygonToolbar srid={projectData.project.srid} />
          {/* Same flag gate covers the off-canvas chrome — toolbar status bar
            and the side compute panel. Without these gates, an existing
            stockpile-mode session could survive a flag flip and the user
            would still see UI for a feature they're no longer entitled to. */}
          {isStockpileAnalysisEnabled && !isSharedLink && (
            <>
              <StockpileToolbar />
              <StockpileComputePanel projectId={projectData.project.id} />
            </>
          )}
          {isPipeAnalysisEnabled && !isSharedLink && <PipeDetectPanel projectId={projectData.project.id} />}
          {!isSharedLink && (
            <>
              <ElevationProfileDrawingHint projectId={projectData.project.id} />
              <ElevationProfilePanel projectId={projectData.project.id} />
            </>
          )}
          {children}
          {/* Heatmap legends — pinned slightly left of the panels so they
            don't fight for the same column. One legend per visible
            RAINFALL_TILE layer (typical case: 0 or 1, but multi-run is
            supported). */}
          {visibleRainfallTileLayers.length > 0 && !hideAnalysisPanels && !isNarrow && (
            <div className='absolute top-4 right-[20rem] z-20 flex flex-col items-end gap-2'>
              {visibleRainfallTileLayers.map((tile) => (
                <RainfallHeatmapLegend key={tile.id} tileLayer={tile} />
              ))}
            </div>
          )}
          {(analysisColumnOpen || (isNarrow && visibleRainfallTileLayers.length > 0)) && (
            // One movable window for ALL top-right analysis panels (rainfall
            // summaries + deviation colorbars/volume panels + surface-difference
            // panels), which is also the single flex column that keeps them from
            // stacking on top of each other. Its position, its height cap and
            // its internal scroll are `AnalysisColumnWindow`'s.
            <AnalysisColumnWindow boundsRef={canvasRegionRef} hidden={hideAnalysisPanels}>
              {/* On a phone `right-[20rem]` is off the left edge, so the
                  heatmap legends join the top strip instead. */}
              {isNarrow &&
                visibleRainfallTileLayers.map((tile) => <RainfallHeatmapLegend key={tile.id} tileLayer={tile} />)}
              {rainfallVectorLayers.map((vec) => {
                const meta = (vec.metadata ?? {}) as {
                  rainfallSummary?: any;
                  rainfallParams?: any;
                };
                if (!meta.rainfallSummary) return null;
                return (
                  <RainfallSummaryPanel
                    key={vec.id}
                    layerId={vec.id}
                    layerName={vec.name ?? ''}
                    summary={meta.rainfallSummary}
                    params={meta.rainfallParams}
                  />
                );
              })}
              {deviationLayers.map((l) => {
                const meta = (l.metadata ?? {}) as Record<string, any>;
                const setting = pointcloudLayerSettings[l.id];
                // Deliberately inline, NOT `resolveScalarRange` (finding
                // F-26): this is why the deviation colorbar is provably
                // unchanged by the clearance work (see the F-4 cross-seam
                // pass in review-final-findings.md §4). It means this
                // fallback (`-0.05`/`0.05`, per-element) and
                // `resolveScalarRange`'s own documented ±0.05 fallback for
                // malformed metadata (`lib/scalarRange.ts`) are two
                // independently maintained copies of the same default —
                // keep them in sync by hand if either changes.
                const fallbackMin = meta.deviationRange?.[0] ?? -0.05;
                const fallbackMax = meta.deviationRange?.[1] ?? 0.05;
                const min = setting?.scalarRangeMin ?? fallbackMin;
                const max = setting?.scalarRangeMax ?? fallbackMax;
                // Volume stats are opt-in (--volume-stats on the deviation
                // run); only render the panel when the layer actually carries
                // a volume sub-object on its deviationStats metadata.
                const volume = (meta.deviationStats?.volume ?? null) as Record<string, unknown> | null;
                // Horizontal mode has no volume pass: the card still shows,
                // saying why it has no figures (C2).
                const horizontalNoStats = !volume && meta.deviationParams?.mode === 'horizontal';
                return (
                  <Fragment key={l.id}>
                    {cutFillLeadBy.get(l.id)?.map(renderCutFillPanel)}
                    <div className='pointer-events-auto flex flex-col gap-2'>
                      <DeviationColorbar
                        layerName={l.name}
                        min={min}
                        max={max}
                        gradientName={setting?.pointCloudMaterialColor ?? 'SPECTRAL'}
                      />
                      {(volume || horizontalNoStats) && (
                        <DeviationVolumePanel
                          projectId={projectId}
                          layerId={l.id}
                          layerName={l.name}
                          volume={volume as any}
                          horizontalNoStats={horizontalNoStats}
                          // Written by the deviation postprocess when the run
                          // asked for a station breakdown (spec §10.3); the
                          // panel's chip opens the dock on this same layer.
                          stationVolumes={(meta.stationVolumes ?? null) as StationVolumesLayerMeta | null}
                          // A paired run's m³ are Cut & fill's — but only
                          // while that layer exists (its half may have failed).
                          showVolumes={showDeviationVolumes(l, layers)}
                          comparison={comparisonReportTarget(l, layers)}
                        />
                      )}
                    </div>
                  </Fragment>
                );
              })}
              {clearanceLayers.map((l) => {
                const meta = (l.metadata ?? {}) as DerivedClearanceLayerMetadata & ScalarRangeMetadata;
                const clearance = meta.clearance;
                if (!clearance) return null;
                const setting = pointcloudLayerSettings[l.id];
                // The colorbar is the ONLY thing on screen that says what the
                // red/blue cloud means (integrity finding I2), so it follows
                // the shading rather than the run: it shows while the cloud is
                // actually ramping over the `Clearance` scalar
                // (PointColorType.CUSTOM_SCALAR, imported here rather than
                // hardcoded — finding F-32 — unlike `postprocess.ts`'s literal
                // `51`, which is unavoidable server-side with no Potree
                // dependency) and disappears when the user switches the layer
                // back to RGB. The card stays either way — the results outlive
                // the colouring.
                const shadingByClearance =
                  setting?.pointCloudMaterialColorSource === PointColorType.CUSTOM_SCALAR &&
                  meta.scalarAttribute === 'Clearance';
                const [min, max] = resolveScalarRangeWithOverride(meta, setting);
                // Zero is not an origin on this bar, it is the profile
                // boundary — and the cold gradation below it IS the near-miss
                // ladder the dock reports, so the ladder's inner and outer
                // bands get ticks. (Only those two: the middle band would
                // crowd them, and the reader needs the shape, not a ruler.)
                // Labels come from the bar's own formatter so a ±0.35 m range
                // shown in cm never grows a metres tick.
                const bands = clearance.params.nearMissBandsM ?? [];
                const markBands = bands.length > 1 ? [bands[0], bands[bands.length - 1]] : bands;
                const barFormat = scalarBarFormat(min, max);
                // `rampSpanM` is P98 of the intrusions (ruling P9), so the
                // worst points routinely sit PAST the end of the ramp and
                // saturate at its darkest red. The card right below this bar
                // prints that worst value; without the `≥` the two contradict
                // each other in the same flex column.
                const worst = clearance.summary.maxIntrusionM;
                return (
                  <div key={l.id} className='pointer-events-auto flex flex-col gap-2'>
                    {shadingByClearance && (
                      <DeviationColorbar
                        layerName={l.name}
                        label={t('clearance.legend.label')}
                        zeroLabel={t('clearance.legend.boundary')}
                        marks={markBands.map((b) => ({ value: -b, label: barFormat.format(-b) }))}
                        min={min}
                        max={max}
                        maxCapped={worst != null && worst > max}
                        gradientName={setting?.pointCloudMaterialColor ?? 'CLEARANCE'}
                      />
                    )}
                    {/* The run's overlay card, on the same `DockPanel` chrome as
                        the deviation and cut/fill cards; it carries the only
                        entry into the clearance dock. */}
                    <ClearancePanel projectId={projectId} layerId={l.id} layerName={l.name} clearance={clearance} />
                  </div>
                );
              })}
              {cutFillRest.map(renderCutFillPanel)}
            </AnalysisColumnWindow>
          )}
          {/* Storm-playback bar — pinned just above the bottom-right
            scale bar / coords. The component returns null when no
            STORM-mode rainfall layer is visible, so it costs nothing
            in the common case. pointer-events on the wrapper are
            disabled so the empty space around the bar doesn't eat
            canvas drags.

            Inline `right` reserves the right column for the rainfall
            results panel (w-96 = 24rem + 2rem outer margin) so the
            bar centres in the *available* viewport width and never
            renders underneath the panel. Inline rather than the
            arbitrary Tailwind value `right-[26rem]` because some JIT
            configs collapse arbitrary rem values to zero on first
            render, causing the bar to shrink to ~10 px wide; an
            inline `style` prop sidesteps that entirely. The bar
            only shows when at least one rainfall STORM layer is
            visible — at which point the panel is also visible — so
            the offset is always correct in practice. */}
          {/* On a phone 26rem is wider than the screen (the bar collapsed to
              nothing); the analysis column is a top strip there, so the bar
              takes the width, above the bottom toolbar. */}
          <div
            className={cn(
              'pointer-events-none absolute left-4 z-20 flex justify-center',
              isNarrow ? 'bottom-14' : 'bottom-12',
            )}
            style={{ right: isNarrow ? '1rem' : '26rem' }}
          >
            <RainfallPlaybackBarMount layers={rainfallVectorLayers} />
          </div>
          {/* The corner column: gizmo, scale bar and coordinates stack here so
              they can never overlap each other, whatever their heights. */}
          <div
            ref={bottomRightRef}
            data-testid='viewer-bottom-right-cluster'
            className={cn(
              'absolute right-2 z-10 flex max-w-[calc(100%-1rem)] flex-col items-end gap-1',
              bottomRightRoom.lift ? 'bottom-14' : 'bottom-2',
            )}
            style={bottomRightRoom.maxWidth != null ? { maxWidth: bottomRightRoom.maxWidth } : undefined}
          >
            {orthoCamera && (
              <OrientationGizmo cameraRef={cameraRef} cameraControlsRef={cameraControlsRef} size={isNarrow ? 84 : 120} />
            )}
            <OrthoScaleBar projectId={projectId} />
            <CoordinateInfoBox projectId={projectId} srid={projectData.project.srid} />
            {showGoogleStreetView ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${positionWGS84?.y},${positionWGS84?.x}`)}`}
                target='_blank'
                rel='noopener noreferrer'
                className='text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 opacity-70 hover:opacity-100 transition-all duration-200'
              >
                © {googleImage ? googleImage.copyright : 'Google Street View'}
              </a>
            ) : null}
          </div>
        </div>
        <FeatureProperties />
        <FragmentProperties />
        <FragmentSpatialTree />
        <LandXMLProperties />
        <LayerProperties />
        {!hideLayers && (
          <ErrorBoundary
            fallback={
              <div className='flex flex-col items-center justify-center px-4'>{t('viewer3d.failedLoadLayers')}</div>
            }
          >
            <Layers projectId={projectId} />
          </ErrorBoundary>
        )}
        {isTouchDevice && isFlying && (
          <Joystick
            visible={showJoystick}
            onMove={() => {
              // Movement is handled in the Controls component via custom events
            }}
            onEnd={() => {
              // Reset is handled in the Controls component via custom events
            }}
          />
        )}
        {clipBoxEnabled && (
          <div
            className={cn(
              'absolute z-10 flex flex-col gap-1',
              toolbarOnRight ? 'top-2 right-12 items-end' : isNarrow ? 'bottom-14 left-2' : 'bottom-12 left-2',
            )}
          >
            <div className='flex gap-1 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md rounded-md border border-slate-200/70 dark:border-stone-700/70 p-1'>
              {(
                [
                  ['NONE', t('viewer3d.clipNone')],
                  ['CLIP_OUTSIDE', t('viewer3d.clipInsideOnly')],
                  ['CLIP_INSIDE', t('viewer3d.clipOutsideOnly')],
                  ['HIGHLIGHT_INSIDE', t('viewer3d.clipHighlight')],
                ] as [ClipBoxMode, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  className={cn(
                    'text-xs px-2 py-1 rounded transition-colors',
                    clipBoxMode === value
                      ? 'bg-slate-800 text-white dark:bg-white dark:text-stone-700'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800',
                  )}
                  onClick={() => setClipBoxState((prev) => ({ ...prev, clipMode: value }))}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className='flex gap-1 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md rounded-md border border-slate-200/70 dark:border-stone-700/70 p-1'>
              {(
                [
                  ['EDIT', t('viewer3d.clipInteractionEdit')],
                  ['VIEW', t('viewer3d.clipInteractionView')],
                ] as [ClipBoxInteractionMode, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  className={cn(
                    'text-xs px-2 py-1 rounded transition-colors',
                    clipBoxState.interactionMode === value
                      ? 'bg-slate-800 text-white dark:bg-white dark:text-stone-700'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800',
                  )}
                  onClick={() => setClipBoxState((prev) => ({ ...prev, interactionMode: value }))}
                >
                  {label}
                </button>
              ))}
              {/* Export is an EDITOR job a shared link can't start. */}
              {pointcloudLayers.length > 0 && !isSharedLink && (
                <>
                  <div className='self-stretch w-px bg-slate-200/80 dark:bg-stone-700/80 ml-auto mr-0.5' />
                  <button
                    className={cn(
                      'text-xs px-2 py-1 rounded transition-colors flex items-center gap-1',
                      !exportClipBoxGate.allowed
                        ? 'text-slate-400 dark:text-stone-500 cursor-pointer'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800',
                    )}
                    onClick={() => {
                      if (!exportClipBoxGate.allowed) {
                        exportClipBoxGate.promptUpgrade();
                        return;
                      }
                      if (!clipBoxState.center) return;
                      setExportClipBoxOpen(true);
                    }}
                  >
                    <DownloadSolid className='size-3.5' />
                    {t('clipboxExport.buttonShort')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        {exportClipBoxGate.allowed && !isSharedLink && clipBoxEnabled && clipBoxState.center && (
          <ClipBoxExportDialog
            open={exportClipBoxOpen}
            onOpenChange={setExportClipBoxOpen}
            projectId={projectId}
            pointCloudLayers={pointCloudLayerOptions}
            defaultLayerId={defaultExportLayerId}
            clipBox={{
              center: clipBoxState.center,
              halfSize: clipBoxState.halfSize,
              rotation: clipBoxState.rotation,
            }}
          />
        )}
        <div
          ref={toolbarRef}
          id='onboarding-viewer-tools'
          // `isolate` + `transform-gpu` pin this toolbar onto its own
          // composited GPU layer with its own stacking context. Without
          // this, Chrome briefly invalidates the buttons' `backdrop-blur`
          // layer whenever a sibling in the same stacking context (e.g. a
          // layer row in the Layers panel running its `transition-colors`
          // hover animation) gets promoted to a compositing layer —
          // visible as a one-frame flicker on the toolbar buttons.
          // Reproduces in production Chrome only; dev paint timing tends
          // to hide it within the same frame.
          className={cn(
            'absolute z-10 flex gap-2 isolate transform-gpu',
            toolbarOnRight ? 'top-2 right-2 flex-col' : 'bottom-2 left-2',
          )}
        >
          {isNarrow ? (
            <>
              <ToggleIconButton
                touch
                active={toolsSheetOpen}
                icon={<ToolSolid />}
                tooltip={t('viewer3d.tools')}
                onClick={() => setToolsSheetOpen(true)}
              />
              <Drawer
                open={toolsSheetOpen}
                onOpenChange={setToolsSheetOpen}
                direction='bottom'
                shouldScaleBackground={false}
              >
                <DrawerContent className='max-h-[80dvh] pb-[env(safe-area-inset-bottom)]'>
                  <div className='mx-auto mt-2 h-1.5 w-12 rounded-full bg-muted' />
                  <DrawerTitle className='px-4 pt-3 pb-2 text-base'>{t('viewer3d.tools')}</DrawerTitle>
                  <ToolbarSheetContext.Provider value={{ close: () => setToolsSheetOpen(false) }}>
                    <div className='flex flex-col overflow-y-auto pb-2'>{toolButtons}</div>
                  </ToolbarSheetContext.Provider>
                </DrawerContent>
              </Drawer>
            </>
          ) : (
            toolButtons
          )}
          {!showGoogleStreetView ? (
            <>
              {isFlying && isTouchDevice && (
                <ToggleIconButton
                  touch={isNarrow}
                  active={showJoystick}
                  icon={<CircleSolid />}
                  tooltip={showJoystick ? t('viewer3d.hideJoystick') : t('viewer3d.showJoystick')}
                  onClick={() => setShowJoystick(!showJoystick)}
                />
              )}
              <CameraModeSwitch
                mode={cameraMode}
                onChange={setCameraMode}
                vertical={toolbarOnRight}
                touch={isNarrow}
              />
            </>
          ) : null}
          <QuickUploadSharedLinkCta projectId={projectId} />
        </div>
        <ViewerContextMenu />
      </div>
      {/* Bottom dock, a layout sibling of the canvas region. Renders nothing
          unless a station-volumes dock is open, and owns its own height (the
          resize grip writes `stationVolumeDockAtom`, this row reads it). */}
      <StationVolumeDock projectId={projectData.project.id} onOpenPdfReport={openStationVolumesReport} />
      {/* The clearance dock shares that slot: the two read the same dock atom
          and the `dataset` discriminator decides which of them renders
          (ruling R2), so only one is ever on screen. */}
      <ClearanceDock projectId={projectData.project.id} onOpenPdfReport={openStationVolumesReport} />
    </div>
  );
};

/** Set inside the narrow-screen Tools sheet: buttons render as labelled rows and close the sheet. */
const ToolbarSheetContext = createContext<{ close: () => void } | null>(null);

const ToggleIconButton = ({
  icon,
  tooltip,
  onClick,
  disabled,
  locked,
  active,
  shortcutKey,
  touch,
}: {
  icon: React.ReactNode;
  tooltip: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  // Visually muted but still clickable — used by feature-gate locked controls so
  // the click can open the upgrade dialog. Cannot use the HTML `disabled` attr
  // because that swallows the click event.
  locked?: boolean;
  active?: boolean;
  shortcutKey?: string;
  /** Finger-sized (40 px) for the narrow-screen bar. */
  touch?: boolean;
}) => {
  const id = useId();
  const sheet = useContext(ToolbarSheetContext);

  if (sheet) {
    return (
      <button
        type='button'
        aria-pressed={active}
        disabled={disabled}
        onClick={() => {
          sheet.close();
          onClick?.();
        }}
        className={cn(
          'flex h-12 w-full items-center gap-3 px-4 text-left text-sm [&>svg]:size-5 [&>svg]:shrink-0 disabled:opacity-70',
          locked && 'opacity-60',
          active && !disabled
            ? 'bg-slate-100 font-medium text-slate-900 dark:bg-stone-800 dark:text-stone-50'
            : 'text-slate-700 active:bg-slate-100 dark:text-stone-200 dark:active:bg-stone-800',
        )}
      >
        {icon}
        <span className='min-w-0 flex-1 truncate'>{tooltip}</span>
        {active && !disabled && <span className='size-2 shrink-0 rounded-full bg-slate-800 dark:bg-stone-100' />}
      </button>
    );
  }

  return (
    <>
      <button
        data-tooltip-id={id}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'rounded-md flex items-center justify-center [&>svg]:stroke-2 border disabled:opacity-70 backdrop-blur-md',
          touch ? 'size-10 [&>svg]:size-5' : 'size-8 [&>svg]:size-4',
          locked && 'opacity-60',
          active && !disabled
            ? 'bg-slate-800/80 text-white border-slate-800/70 hover:bg-slate-800 active:bg-slate-900 dark:bg-white dark:border-slate-200/70 dark:text-stone-700'
            : 'bg-white/70 border-slate-200/70 backdrop-blur-md text-slate-700 hover:bg-slate-50 active:bg-slate-100 dark:bg-stone-900/80 dark:border-stone-700/70 dark:text-stone-100',
        )}
      >
        {icon}
      </button>
      <PortalTooltip id={id} delayHide={0} delayShow={120}>
        {shortcutKey ? (
          <span className='flex items-center gap-2'>
            {tooltip}
            <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1.5 py-0.5 rounded text-[10px]'>
              {shortcutKey}
            </kbd>
          </span>
        ) : (
          tooltip
        )}
      </PortalTooltip>
    </>
  );
};

/** Screen heights a fully deflected joystick crosses per second in the orbit modes. */
const JOYSTICK_SCREENS_PER_SECOND = 0.75;
const _panForward = new THREE.Vector3();
const _panScreenUp = new THREE.Vector3();
const _panScreenRight = new THREE.Vector3();
const _panTarget = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 0, 1);

/**
 * Joystick pan for the orbit modes: moves camera and target together across
 * the ground (Z up), stick up = towards the top of the screen.
 *
 * Speed is a fraction of the visible view height, not metres per second, so
 * it feels the same zoomed in or out and in ortho, whose camera sits 1000 m
 * out and whose `zoom` (not distance) sets the scale. The stick is analog:
 * a half deflection pans at half speed.
 */
function panAlongGround(
  camera: THREE.Camera,
  controls: CameraControlsImpl,
  stick: { x: number; y: number },
  delta: number,
) {
  let viewHeight: number;
  if (camera instanceof THREE.OrthographicCamera) {
    viewHeight = Math.abs(camera.top - camera.bottom) / Math.max(camera.zoom, 1e-6);
  } else {
    const p = camera as THREE.PerspectiveCamera;
    const vFov = THREE.MathUtils.degToRad(p.getEffectiveFOV?.() ?? p.fov);
    viewHeight = 2 * controls.distance * Math.tan(vFov / 2);
  }

  // Screen-up on the ground: the view direction's ground component when the
  // camera looks out, the camera's up vector's when it looks down. Both point
  // the same way in between, so their sum is never degenerate.
  camera.getWorldDirection(_panForward).setZ(0);
  _panScreenUp.set(0, 1, 0).applyQuaternion(camera.quaternion).setZ(0);
  _panScreenUp.add(_panForward);
  if (_panScreenUp.lengthSq() < 1e-12) return;
  _panScreenUp.normalize();
  // camera-controls never rolls, so screen-right is already level.
  _panScreenRight.crossVectors(_panScreenUp, _worldUp).normalize();

  const magnitude = Math.min(1, Math.hypot(stick.x, stick.y));
  const angle = Math.atan2(stick.y, -stick.x); // Joystick inverts x.
  const step = viewHeight * JOYSTICK_SCREENS_PER_SECOND * magnitude * Math.min(delta, 0.1);

  const dx = Math.cos(angle) * step;
  const dy = Math.sin(angle) * step;
  const moveX = _panScreenRight.x * dx + _panScreenUp.x * dy;
  const moveY = _panScreenRight.y * dx + _panScreenUp.y * dy;

  controls.getTarget(_panTarget);
  controls.setLookAt(
    camera.position.x + moveX,
    camera.position.y + moveY,
    camera.position.z,
    _panTarget.x + moveX,
    _panTarget.y + moveY,
    _panTarget.z,
    false,
  );
}

// CameraControls Z-up: azimuth rotates around Z, polar = angle from +Z
const GIZMO_AXES = [
  { dir: [1, 0, 0] as const, label: 'E', color: '#e74c3c', azimuth: Math.PI / 2, polar: Math.PI / 2 },
  { dir: [0, 1, 0] as const, label: 'N', color: '#2ecc71', azimuth: 0, polar: Math.PI / 2 },
  { dir: [0, 0, 1] as const, label: 'U', color: '#3498db', azimuth: 0, polar: 0.01 },
];

const OrientationGizmo = ({
  cameraRef,
  cameraControlsRef,
  size = 120,
}: {
  cameraRef: RefObject<Camera | null>;
  cameraControlsRef: RefObject<CameraControlsImpl | null>;
  /** Rendered size in px; the drawing itself is always in 120 units. */
  size?: number;
}) => {
  const SIZE = 120;
  const HALF = SIZE / 2;
  const AXIS_LEN = 38;
  const [projected, setProjected] = useState(GIZMO_AXES.map((a) => ({ ...a, x: 0, y: 0, z: 0 })));
  const rafRef = useRef(0);

  useEffect(() => {
    const mat = new THREE.Matrix3();
    const v = new THREE.Vector3();

    const update = () => {
      rafRef.current = requestAnimationFrame(update);
      if (!cameraRef.current) return;
      // Extract the 3x3 rotation (inverse = transpose for rotation-only)
      mat.setFromMatrix4(cameraRef.current.matrixWorldInverse);
      const next = GIZMO_AXES.map((a) => {
        v.set(a.dir[0], a.dir[1], a.dir[2]).applyMatrix3(mat);
        return { ...a, x: v.x * AXIS_LEN, y: -v.y * AXIS_LEN, z: v.z };
      });
      setProjected(next);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafRef.current);
  }, [cameraRef]);

  const handleClick = (azimuth: number, polar: number) => {
    cameraControlsRef.current?.rotateTo(azimuth, polar, true);
  };

  // Sort by z so front axes render on top
  const sorted = [...projected].sort((a, b) => a.z - b.z);

  return (
    // Drawn in a 120-unit box and scaled to `size`: on a phone the corner
    // column shares a short canvas with the top analysis strip.
    <div className='shrink-0 pointer-events-auto' style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        {sorted.map((a) => (
          <g key={a.label} style={{ cursor: 'pointer' }} onClick={() => handleClick(a.azimuth, a.polar)}>
            <line
              x1={HALF}
              y1={HALF}
              x2={HALF + a.x}
              y2={HALF + a.y}
              stroke={a.color}
              strokeWidth={2}
              strokeOpacity={a.z < 0 ? 0.3 : 0.9}
            />
            <circle cx={HALF + a.x} cy={HALF + a.y} r={12} fill={a.color} fillOpacity={a.z < 0 ? 0.3 : 0.9} />
            <text
              x={HALF + a.x}
              y={HALF + a.y}
              textAnchor='middle'
              dominantBaseline='central'
              fill='white'
              fontSize={10}
              fontWeight='bold'
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {a.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const SceneBackground = ({ color }: { color: string | null }) => {
  const scene = useThree((state) => state.scene);
  useEffect(() => {
    scene.background = color ? new THREE.Color(color) : null;
  }, [scene, color]);
  return null;
};

const calculateControlSpeed = (camera: Camera, canvas: HTMLCanvasElement, flying: boolean, thirdperson: boolean) => {
  if (camera instanceof THREE.OrthographicCamera) return 0.6;
  const H = canvas.clientHeight;
  const fov = camera.getEffectiveFOV();
  const vfov = THREE.MathUtils.degToRad(fov);
  const exact = (H / Math.PI) * Math.atan(Math.tan(vfov / 2) * (1 / H));
  if (flying && thirdperson) return exact * 2;
  return -exact;
};

type KeyMap = {
  KeyW: boolean;
  KeyA: boolean;
  KeyS: boolean;
  KeyD: boolean;
  KeyQ: boolean;
  KeyE: boolean;
  Space: boolean;
  ShiftLeft: boolean;
};

const useKeyboard = (): KeyMap => {
  const [keys, setKeys] = useState<KeyMap>({
    KeyW: false,
    KeyA: false,
    KeyS: false,
    KeyD: false,
    KeyQ: false,
    KeyE: false,
    Space: false,
    ShiftLeft: false,
  });

  useEffect(() => {
    const el = document.getElementById('canvas-container');
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code in keys) setKeys((k) => ({ ...k, [e.code as keyof KeyMap]: true }));
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code in keys) setKeys((k) => ({ ...k, [e.code as keyof KeyMap]: false }));
    };
    // Reset every key when the viewer/window loses focus so we never get
    // stuck "flying" because a keyup landed on a different target.
    const resetKeys = () =>
      setKeys((k) => {
        const cleared = { ...k };
        for (const code in cleared) cleared[code as keyof KeyMap] = false;
        return cleared;
      });
    // keydown is gated on the viewer element (flight only starts when focused),
    // but keyup must be global: if the cursor/focus leaves the element while a
    // key is held, the keyup fires on a different target and would otherwise
    // never reach `el`, leaving the key stuck "down".
    el.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', resetKeys);
    return () => {
      el.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', resetKeys);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return keys;
};

const Controls = ({
  projectId,
  cameraRef,
  cameraControlsRef,
  skipPositionSyncRef,
  position,
  setPosition,
  flying,
  thirdperson,
  cameraMode,
  isTouchDevice,
  initialAngle,
  initialPosition,
}: {
  projectId: string;
  cameraRef: RefObject<Camera | null>;
  cameraControlsRef: RefObject<CameraControlsImpl | null>;
  skipPositionSyncRef: RefObject<boolean>;
  position: THREE.Vector3;
  setPosition: (value: SetStateAction<THREE.Vector3>) => void;
  flying: boolean;
  thirdperson: boolean;
  cameraMode: CameraMode;
  isTouchDevice: boolean;
  initialAngle: number;
  initialPosition: { x: number; y: number; z: number } | null;
}) => {
  const camera = useThree((state) => state.camera);
  const canvas = useThree((state) => state.gl.domElement);
  const keys = useKeyboard();
  const hasOrientedToInitialAngle = useRef(false);
  const joystickInputRef = useRef({ x: 0, y: 0 });
  const setCameraMapState = useSetAtom(cameraMapStateAtom(projectId));
  const setOrthoMetersPerPixel = useSetAtom(orthoMetersPerPixelAtom(projectId));
  const size = useThree((s) => s.size);
  const lastCameraWriteRef = useRef(0);
  const lastRotationWriteRef = useRef(0);

  const cameraStateKey = `@actegon.gis.cameraState.${projectId}`;
  const storedCameraRef = useRef<StoredCameraState | null | undefined>(undefined);
  if (storedCameraRef.current === undefined) {
    try {
      const raw = sessionStorage.getItem(cameraStateKey);
      storedCameraRef.current = raw ? JSON.parse(raw) : null;
    } catch {
      storedCameraRef.current = null;
    }
  }

  const up = useMemo(() => new THREE.Vector3(0, 0, 1), []);
  const forward = useMemo(() => new THREE.Vector3(), []);
  const right = useMemo(() => new THREE.Vector3(), []);
  const move = useMemo(() => new THREE.Vector3(), []);

  const [rotateSpeed, setRotateSpeed] = useState(calculateControlSpeed(camera, canvas, flying, thirdperson));

  const isOrthoCamera = camera instanceof THREE.OrthographicCamera;

  // camera-controls has one `dollySpeed` for wheel and pinch. Pinch scales by
  // 0.95^(fingerΔpx / 8 × dollySpeed), so the wheel-tuned 0.5 zooms ~1.6× when
  // the fingers spread 2.5× — far behind the fingers. Phones and tablets (a
  // coarse primary pointer, so not touch-screen laptops, whose wheel this
  // would speed up) get double, which roughly tracks the pinch.
  const coarsePointer = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true,
    [],
  );
  const dollySpeed = (isOrthoCamera ? 1 : 0.5) * (coarsePointer ? 2 : 1);

  // Expose joystick input handler
  useEffect(() => {
    if (!isTouchDevice) return;

    const handleJoystickMove = (event: CustomEvent<{ x: number; y: number }>) => {
      joystickInputRef.current = event.detail;
    };

    const handleJoystickEnd = () => {
      joystickInputRef.current = { x: 0, y: 0 };
    };

    window.addEventListener('joystick-move' as any, handleJoystickMove);
    window.addEventListener('joystick-end' as any, handleJoystickEnd);

    return () => {
      window.removeEventListener('joystick-move' as any, handleJoystickMove);
      window.removeEventListener('joystick-end' as any, handleJoystickEnd);
    };
  }, [isTouchDevice]);

  // Track whether we need a deferred sync (cameraControls wasn't ready when effect ran)
  const needsSyncRef = useRef(false);

  // A camera-mode switch in flight: captured before R3F swaps the camera
  // object, applied once the swap lands. See the transition effect below.
  const pendingTransitionRef = useRef<{ from: CameraMode; to: CameraMode; pose: CameraPose } | null>(null);

  const applyPositionSync = useCallback(() => {
    if (!cameraControlsRef.current) return false;

    if (cameraRef.current && cameraRef.current !== camera && camera instanceof THREE.OrthographicCamera) {
      camera.position.copy(cameraRef.current.position);
    }

    cameraRef.current = camera;

    const stored = storedCameraRef.current;
    // Only a real mount restores the snapshot. It used to run on `cameraChanged`
    // too — every camera-mode switch swaps the camera object — which replayed a
    // pose up to 500 ms old carrying the *other* projection's distance and zoom
    // (ortho orbits at 1000 m and zooms 1–500, perspective at ~50 m and 0.4–8).
    // That is the camera "úszik el" in the bug report; a mode switch now goes
    // through `cameraTransition` instead.
    const isInitialRestore = !hasOrientedToInitialAngle.current;

    if (isInitialRestore && stored) {
      hasOrientedToInitialAngle.current = true;
      // Restore full camera state from sessionStorage
      cameraControlsRef.current.setLookAt(
        stored.position[0],
        stored.position[1],
        stored.position[2],
        stored.target[0],
        stored.target[1],
        stored.target[2],
        false,
      );
      cameraControlsRef.current.rotateTo(stored.azimuth, stored.polar, false);
      cameraControlsRef.current.dollyTo(stored.distance, false);
      if (stored.zoom != null) {
        // zoomTo writes camera-controls' internal _zoom (the source of truth);
        // a direct camera.zoom assignment is overwritten by the update(0) below.
        cameraControlsRef.current.zoomTo(stored.zoom, false);
      }
      cameraControlsRef.current.update(0);
      return true;
    }

    if (!hasOrientedToInitialAngle.current) {
      hasOrientedToInitialAngle.current = true;
      // First-ever load: anchor on the project's initialPosition prop rather
      // than the atom-derived `position`. The viewer-state atom is reset on
      // mount in a layout effect on the DOM root, but R3F renders into its
      // own root, so the updated atom value may not have reached this render
      // by the time this first sync runs. Using the prop avoids a transient
      // (0,0,0) anchor that the flying-mode early-return below would lock in.
      const ix = initialPosition?.x ?? position.x;
      const iy = initialPosition?.y ?? position.y;
      const iz = initialPosition?.z ?? position.z;
      cameraControlsRef.current.rotateTo(0, Math.PI / 2, false);
      cameraControlsRef.current
        .normalizeRotations()
        .setLookAt(ix - 0.00001, iy - 0.00001, iz - 0.00001, ix, iy, iz, false);
      cameraControlsRef.current.rotateTo(-initialAngle * THREE.MathUtils.DEG2RAD, Math.PI / 2, false);
    } else {
      // Subsequent position syncs: preserve current camera rotation/distance
      const az = cameraControlsRef.current.azimuthAngle;
      const pol = cameraControlsRef.current.polarAngle;
      const dist = orbitDistanceOrDefault(cameraControlsRef.current.distance, camera, flying && thirdperson);
      const zoom = camera.zoom;

      cameraControlsRef.current
        .normalizeRotations()
        .setLookAt(
          position.x - 0.00001,
          position.y - 0.00001,
          position.z - 0.00001,
          position.x,
          position.y,
          position.z,
          false,
        );

      cameraControlsRef.current.rotateTo(az, pol, false);
      cameraControlsRef.current.dollyTo(dist, false);
      camera.zoom = zoom;
      camera.updateProjectionMatrix();
    }
    // Flush CameraControls so camera.position is updated immediately.
    // Without this, components that depend on camera.position (e.g. GeoJson
    // nearbyFeatures filter) see the stale default (0,0,5) after a Canvas remount.
    cameraControlsRef.current.update(0);
    return true;
  }, [camera, cameraControlsRef, cameraRef, position, initialAngle, initialPosition, flying, thirdperson]);

  useEffect(() => {
    if (skipPositionSyncRef.current) {
      skipPositionSyncRef.current = false;
      return;
    }
    // A mode switch owns the pose it lands on; re-anchoring the camera on the
    // floating origin in between is exactly the second jump users saw. The ref
    // still has to follow the swap, though — the orientation gizmo, the map
    // state and the session snapshot all read the camera through it, and
    // `applyPositionSync` (skipped here) is what normally advances it.
    if (pendingTransitionRef.current) {
      cameraRef.current = camera;
      return;
    }
    if (flying && cameraRef.current === camera && !(camera instanceof THREE.OrthographicCamera)) return;

    if (!applyPositionSync()) {
      // CameraControls ref not ready yet (Canvas just remounted) — defer
      needsSyncRef.current = true;
    } else {
      needsSyncRef.current = false;
    }
  }, [cameraControlsRef, position, flying, camera, applyPositionSync]);

  // Deferred sync: when CameraControls becomes available after a Canvas remount,
  // apply the pending position sync that was missed.
  useFrame(() => {
    if (needsSyncRef.current && cameraControlsRef.current) {
      needsSyncRef.current = false;
      applyPositionSync();
    }
  });

  // Pending saved-view camera apply. Held until the live camera's projection type
  // matches the saved `ortho` flag, because applying a saved view may first flip
  // the ortho/perspective toggle (which swaps the camera object a frame later).
  const pendingCameraApplyRef = useRef<SavedViewCamera | null>(null);

  useEffect(() => {
    return viewerEvents.on('applyCameraState', (cam) => {
      pendingCameraApplyRef.current = cam;
    });
  }, []);

  useFrame(() => {
    const cam = pendingCameraApplyRef.current;
    if (!cam || !cameraControlsRef.current) return;
    const isOrtho = camera instanceof THREE.OrthographicCamera;
    if (isOrtho !== cam.ortho) return; // wait for the mode switch to land

    pendingCameraApplyRef.current = null;
    cameraControlsRef.current.setLookAt(
      cam.position[0],
      cam.position[1],
      cam.position[2],
      cam.target[0],
      cam.target[1],
      cam.target[2],
      true,
    );
    cameraControlsRef.current.rotateTo(cam.azimuth, cam.polar, true);
    cameraControlsRef.current.dollyTo(cam.distance, true);
    // Route zoom through camera-controls (zoomTo updates its internal _zoom,
    // the source of truth). Setting camera.zoom directly is clobbered back to
    // _zoom on the next per-frame controls.update() — which silently dropped
    // the saved orthographic zoom level.
    cameraControlsRef.current.zoomTo(cam.zoom, true);

    // Stop the position-sync effect from snapping the floating origin back, and
    // recenter it on the saved target.
    skipPositionSyncRef.current = true;
    setPosition(new THREE.Vector3(cam.target[0], cam.target[1], cam.target[2]));
  });

  useFrame((_, delta) => {
    if (!flying) return;

    camera.getWorldDirection(forward);
    // forward.z = 0;
    forward.normalize();

    right.crossVectors(up, forward).normalize();
    const localUp = new THREE.Vector3().crossVectors(forward, right).normalize();

    // Orbit modes (third person, ortho): the joystick pans across the ground
    // like a map instead of flying along the view direction — in ortho that
    // direction is straight down, so "forward" moved nothing on screen.
    const joystick = isTouchDevice ? joystickInputRef.current : null;
    const panWithJoystick = thirdperson && !!joystick && (joystick.x !== 0 || joystick.y !== 0);
    if (panWithJoystick && cameraControlsRef.current) {
      panAlongGround(camera, cameraControlsRef.current, joystick, delta);
    }

    // Combine keyboard and joystick input
    const joyF = joystick && !thirdperson ? joystick.y : 0;
    const joyR = joystick && !thirdperson ? joystick.x : 0;
    const f = (keys.KeyW ? 1 : 0) - (keys.KeyS ? 1 : 0) + joyF;
    const r = (keys.KeyA ? 1 : 0) - (keys.KeyD ? 1 : 0) + joyR;
    const u = keys.Space || keys.KeyE ? 1 : 0;
    const d = keys.KeyQ ? 1 : 0;

    move.set(0, 0, 0);
    if (f) move.addScaledVector(forward, f);
    if (r) move.addScaledVector(right, r);
    if (u) move.addScaledVector(localUp, u);
    if (d) move.addScaledVector(localUp, -1);

    if (move.lengthSq() > 0 && cameraRef.current && cameraControlsRef.current) {
      move.normalize();
      const speed = 10;
      const v = (keys.ShiftLeft ? 4 : 1) * speed * delta;
      const newPosition = cameraRef.current.position.clone().addScaledVector(move, v);
      if (!thirdperson) {
        cameraControlsRef.current.moveTo(newPosition.x, newPosition.y, newPosition.z, false);
      } else {
        const newTarget = cameraControlsRef.current.getTarget(new THREE.Vector3()).addScaledVector(move, v);
        cameraControlsRef.current.setLookAt(
          newPosition.x,
          newPosition.y,
          newPosition.z,
          newTarget.x,
          newTarget.y,
          newTarget.z,
          false,
        );
      }
    }
  });

  // Publish camera state to atom for MapView consumption (throttled to ~50ms)
  useFrame(() => {
    const now = performance.now();
    if (now - lastCameraWriteRef.current < 50) return;
    lastCameraWriteRef.current = now;
    if (!cameraRef.current) return;

    const dir = new THREE.Vector3();
    cameraRef.current.getWorldDirection(dir);
    const rad = Math.atan2(dir.x, dir.y);
    const rotationDeg = THREE.MathUtils.radToDeg(rad) + 180;

    let fovBase = 60;
    if (cameraRef.current.type !== 'OrthographicCamera') {
      const perspCam = cameraRef.current as THREE.PerspectiveCamera;
      const halfFovRad = (perspCam.getEffectiveFOV() / 2) * (Math.PI / 180);
      fovBase = 2 * 25 * Math.tan(halfFovRad) * perspCam.aspect;
    }

    const trackPos =
      thirdperson && cameraControlsRef?.current
        ? cameraControlsRef.current.getTarget(new THREE.Vector3())
        : cameraRef.current.position;

    setCameraMapState({
      position: [trackPos.x, trackPos.y, trackPos.z],
      rotationDeg,
      fovBase,
    });

    if (cameraRef.current instanceof THREE.OrthographicCamera) {
      const o = cameraRef.current;
      const worldHeight = Math.abs(o.top - o.bottom) / Math.max(o.zoom, 1e-6);
      setOrthoMetersPerPixel(worldHeight / Math.max(size.height, 1));
    } else {
      setOrthoMetersPerPixel(null);
    }

    // Persist camera state to sessionStorage (throttled to ~500ms)
    if (cameraControlsRef?.current && cameraRef.current && now - lastRotationWriteRef.current >= 500) {
      lastRotationWriteRef.current = now;
      const t = cameraControlsRef.current.getTarget(new THREE.Vector3());
      const p = cameraRef.current.position;
      const state: StoredCameraState = {
        azimuth: cameraControlsRef.current.azimuthAngle,
        polar: cameraControlsRef.current.polarAngle,
        distance: cameraControlsRef.current.distance,
        zoom: cameraRef.current.zoom,
        position: [p.x, p.y, p.z],
        target: [t.x, t.y, t.z],
        ...cameraModeToFlags(cameraMode),
      };
      sessionStorage.setItem(cameraStateKey, JSON.stringify(state));
    }
  });

  /**
   * Camera-mode transitions — what replaced the `dollyTo(isOrtho ? 1000 : 50)`
   * reset that used to live here.
   *
   * Two things made a mode switch throw the camera across the site. The reset
   * itself knew nothing about what the user was looking at, and drei rebuilds
   * the whole `CameraControls` instance when the camera object changes
   * (`useMemo(() => new Impl(camera), [camera])`), so the new one starts with
   * its target at the world origin — at UTM coordinates that is an orbit radius
   * of several million metres.
   *
   * So the pose is captured here, in the commit where the mode prop changed and
   * the *old* camera and controls are still current, and applied by the frame
   * below once the swap has landed. `cameraTransition` converts the framing
   * between the projections; the view direction never changes.
   */
  const perspectiveFovRef = useRef(60);
  useEffect(() => {
    if (camera instanceof THREE.PerspectiveCamera) perspectiveFovRef.current = camera.getEffectiveFOV();
  }, [camera]);

  const appliedModeRef = useRef(cameraMode);
  useEffect(() => {
    const from = appliedModeRef.current;
    if (from === cameraMode) return;
    appliedModeRef.current = cameraMode;
    const controls = cameraControlsRef.current;
    if (!controls) return;
    const target = controls.getTarget(new THREE.Vector3());
    const eye = controls.getPosition(new THREE.Vector3());
    const forward = camera.getWorldDirection(new THREE.Vector3());
    pendingTransitionRef.current = {
      from,
      to: cameraMode,
      pose: {
        position: [eye.x, eye.y, eye.z],
        target: [target.x, target.y, target.z],
        forward: [forward.x, forward.y, forward.z],
        zoom: camera.zoom,
      },
    };
  }, [cameraMode, camera, cameraControlsRef]);

  useFrame(() => {
    const pending = pendingTransitionRef.current;
    const controls = cameraControlsRef.current;
    if (!pending || !controls) return;
    // A saved view spells out the whole pose, so it wins outright — applying a
    // view flips the mode first, and both would otherwise write the same frame.
    if (pendingCameraApplyRef.current) {
      pendingTransitionRef.current = null;
      return;
    }
    const orthoNow = camera instanceof THREE.OrthographicCamera;
    if (orthoNow !== (pending.to === 'ORTHO')) return; // wait for the swap to land
    pendingTransitionRef.current = null;

    const next = cameraTransition(pending.from, pending.to, pending.pose, {
      fovDeg: perspectiveFovRef.current,
      // drei sizes the ortho frustum from the canvas, so its height is the
      // viewport height in world units at zoom 1 either way.
      orthoFrustumHeight: orthoNow
        ? Math.abs((camera as THREE.OrthographicCamera).top - (camera as THREE.OrthographicCamera).bottom)
        : size.height,
    });

    controls.setLookAt(
      next.position[0],
      next.position[1],
      next.position[2],
      next.target[0],
      next.target[1],
      next.target[2],
      false,
    );
    controls.zoomTo(next.zoom, false);
    controls.update(0);

    // Recentre the floating origin the way `jumpToTarget` and the saved-view
    // apply do: on the camera in first person, on the orbit target otherwise.
    const origin = pending.to === 'FIRST_PERSON' ? next.position : next.target;
    skipPositionSyncRef.current = true;
    setPosition(new THREE.Vector3(origin[0], origin[1], origin[2]));
  });

  // Jump to target (from measurements, etc.)
  useEffect(() => {
    return viewerEvents.on('jumpToTarget', (jumpTarget) => {
      if (!cameraControlsRef.current) return;
      const target = new THREE.Vector3(jumpTarget.x, jumpTarget.y, jumpTarget.z);

      if (flying && !thirdperson) {
        // First-person: place camera 2m above the target, then orient to look down at it.
        // camera-controls uses Y-up internally while the scene is Z-up, so we convert
        // the look direction into camera-controls spherical coords via the same
        // rotation matrix the position-sync effect uses.
        const offset = target.clone().add(new THREE.Vector3(0, 5, 2));
        cameraControlsRef.current.moveTo(offset.x, offset.y, offset.z, false);

        // Direction from camera (offset) toward the measurement target in world space
        const lookDir = target.clone().sub(offset).normalize();
        // camera-controls expects the *opposite* direction (from target to camera) in its Y-up frame
        const camCtrlDir = lookDir
          .clone()
          .negate()
          .applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
        const spherical = new THREE.Spherical().setFromVector3(camCtrlDir);
        cameraControlsRef.current.rotateTo(spherical.theta, spherical.phi, false);

        skipPositionSyncRef.current = true;
        setPosition(offset);
      } else {
        const currentTarget = cameraControlsRef.current.getTarget(new THREE.Vector3());
        const camDir = camera.position.clone().sub(currentTarget);
        // Camera on its own target has no direction to keep; landing at
        // distance 0 would freeze rotation (see orbitDistanceOrDefault).
        if (camDir.lengthSq() < 1e-12) camDir.set(0, -0.3, 1);
        camDir.normalize();
        const camPos = target.clone().add(camDir.multiplyScalar(5));
        cameraControlsRef.current.setLookAt(camPos.x, camPos.y, camPos.z, target.x, target.y, target.z, true);
        skipPositionSyncRef.current = true;
        setPosition(target.clone());
      }
    });
  }, [camera, flying, thirdperson, setPosition]);

  // "Look at section" (spec §11.3). Additive: the walk toolbar's own follow
  // emits `setCenterTarget` and keeps doing so — this only adds a pose, and
  // nothing in AlignmentWalkToolbar.tsx changes.
  //
  // The mode branch is `lookAtCameraCommand`'s (finding I8): first person pins
  // the orbit radius at ~0, so the pose has to become `moveTo` + `rotateTo`
  // there, exactly as `jumpToTarget` above does. The rule lives in
  // `lib/lookAtCamera.ts` because this file cannot be mounted in jsdom.
  useEffect(() => {
    return viewerEvents.on('lookAt', (pose) => {
      const controls = cameraControlsRef.current;
      if (!controls) return;
      const cmd = lookAtCameraCommand(pose, { flying, thirdperson });
      if (cmd.kind === 'firstPerson') {
        controls.moveTo(cmd.position[0], cmd.position[1], cmd.position[2], false);
        controls.rotateTo(cmd.theta, cmd.phi, false);
      } else {
        controls.setLookAt(
          cmd.position[0],
          cmd.position[1],
          cmd.position[2],
          cmd.target[0],
          cmd.target[1],
          cmd.target[2],
          true,
        );
      }
      // Stop the position-sync effect from snapping the floating origin back,
      // and recentre it on what we are now looking at — the same two lines the
      // saved-view apply does.
      skipPositionSyncRef.current = true;
      setPosition(new THREE.Vector3(cmd.origin[0], cmd.origin[1], cmd.origin[2]));
    });
  }, [flying, thirdperson, setPosition]);

  // Re-center orbit target (third person only)
  useEffect(() => {
    return viewerEvents.on('setCenterTarget', (target) => {
      if (!cameraControlsRef.current || !thirdperson) return;
      const t = new THREE.Vector3(target.x, target.y, target.z);
      cameraControlsRef.current.setTarget(t.x, t.y, t.z, true);
      skipPositionSyncRef.current = true;
      setPosition(t.clone());
    });
  }, [thirdperson, setPosition]);

  return (
    <CameraControls
      minDistance={flying && thirdperson && !isOrthoCamera ? 0.5 : Number.EPSILON}
      maxDistance={flying && thirdperson ? (isOrthoCamera ? Infinity : 10000) : 0.00001}
      minPolarAngle={0.017}
      maxPolarAngle={Math.PI - 0.017}
      smoothTime={0}
      draggingSmoothTime={0.03}
      azimuthRotateSpeed={rotateSpeed}
      polarRotateSpeed={rotateSpeed}
      dollySpeed={dollySpeed}
      truckSpeed={isOrthoCamera ? 1 : 6}
      enabled
      ref={cameraControlsRef}
      camera={camera}
      restThreshold={0.05}
      onRest={() => setRotateSpeed(calculateControlSpeed(camera, canvas, flying, thirdperson))}
      mouseButtons={{
        left: CameraControlsImpl.ACTION.ROTATE,
        right: flying && thirdperson ? CameraControlsImpl.ACTION.TRUCK : CameraControlsImpl.ACTION.NONE,
        middle: flying && thirdperson ? CameraControlsImpl.ACTION.DOLLY : CameraControlsImpl.ACTION.NONE,
        wheel:
          flying && thirdperson && !isOrthoCamera ? CameraControlsImpl.ACTION.DOLLY : CameraControlsImpl.ACTION.ZOOM,
      }}
      // Two fingers mirror the mouse's right button: in the orbit modes a
      // two-finger drag pans (truck) while the pinch dollies (perspective) or
      // zooms (ortho), the way every map app behaves. First person stays
      // pinch-to-zoom only — it has no orbit radius to pan around.
      touches={{
        one: CameraControlsImpl.ACTION.TOUCH_ROTATE,
        two:
          flying && thirdperson
            ? isOrthoCamera
              ? CameraControlsImpl.ACTION.TOUCH_ZOOM_TRUCK
              : CameraControlsImpl.ACTION.TOUCH_DOLLY_TRUCK
            : CameraControlsImpl.ACTION.TOUCH_ZOOM,
        three: flying && thirdperson ? CameraControlsImpl.ACTION.TOUCH_TRUCK : CameraControlsImpl.ACTION.TOUCH_ZOOM,
      }}
      maxZoom={isOrthoCamera ? 500 : 8}
      minZoom={isOrthoCamera ? 1 : 0.4}
      makeDefault
    />
  );
};

Viewer.displayName = 'Viewer';

export default Viewer;
